// ABVME web — main window controller (port of views/main_window.py + viewmodel).
import { api, ApiError, downloadBlob, downloadUrl, request, setGuestFingerprint, setUnauthorizedHandler, setWorkspaceExpiredHandler, xhrUpload } from "./api.js";
import * as local from "./localstore.js";
import { APP_ICONS, preloadIcons } from "./icons.js";
import { previewText, readPack } from "./pack.js";
import { downloadPacks, uploadFiles } from "./transfer.js";
import { discardTarget, fileFromHandle, filesFromRecent, pickFiles, pickFolderFiles, pickSaveTarget, writeFile } from "./files.js";
import { askYesNo, closeModal, contextMenu, messageBox, openModal, promptText, showError, showInfo, showWarning } from "./dialogs.js";
import { classifyDrop, DropAction, DropOverlay, filesFromDrop, previewDecision } from "./drop.js";
import {
  AVAILABLE_TYPES,
  COLOR_SPACE_LABELS,
  DEFAULT_SETTINGS,
  FILTER_MODE_LABELS,
  OPEN_FOLDER_CONFIRM_AFTER,
  OPEN_SUFFIXES,
  OPTION_LABELS,
  SAVE_OPTION_HELP,
  TEXTURE_FORMAT_CHOICES,
  TEXTURE_REPLACE_SUFFIXES,
  WRAP_MODE_LABELS,
} from "./options.js";
import { AssetTable, closeFilterMenu, COLUMNS } from "./table.js";
import { createAtlasToolkit } from "./atlas-toolkit.js";
import { createGameFiles } from "./gamefiles.js";
import { geometryFromObj, geometryFromPack, MeshViewer } from "./mesh-viewer.js";
import { $, $$, debounce, deviceName, escapeHtml, fmtDate, formatByteSize, hasUnityFsHeader, isMobile, relTime, setPresent, suffix, suffixInContainer } from "./util.js";
import { PhotoViewer } from "./viewer.js";
import { workspaceName } from "./workspace-name.js";

const STATUS_TIMEOUT = 10_000;
/** While the tab is in use, tell the server at most this often (it deletes idle workspaces). */
const KEEPALIVE_MS = 5 * 60 * 1000;

// ======================================================================
// State
// ======================================================================

const state = {
  user: null,
  guest: null, // guest config while using ABVME without an account
  settings: { ...DEFAULT_SETTINGS },
  tab: local.tabId(), // this browser tab: its workspace is kept in local storage
  workspace: null, // {id (server session), name}
  files: new Map(), // id -> file row
  edits: new Map(), // `${fid}:${pid}` -> edit
  savedEdits: new Set(), // `${key}@${edit.created_at}` of replacements included in a save
  assets: [], // every object in the workspace
  selected: [],
  busy: 0,
  previewToken: 0,
  previewAsset: null,
  previewItem: null, // {preview, dump, texture_options, image} of previewAsset
  previewUrl: null, // object URL of the shown image
  itemCache: new Map(), // `${asset.key}:${sig}` -> item (LRU)
  status: null,
  selectMode: false,
  serverGone: false, // the server dropped its copy of the files (an hour idle); sent again when needed
  serverReady: new Set(), // file ids the server's session holds
  partSize: 0, // multipart chunk size the server asked for
};

let table;
let viewer;
let overlay;

/** Signed-in users talk to /api, guests to /api/guest (same routes below that). */
const guestMode = () => !state.user && Boolean(state.guest);
const apiBase = () => (state.user ? "/api" : "/api/guest");
/** Whose workspace the tab holds; a different sign-in starts empty. */
const ownerKey = () => (state.user ? `u:${state.user.id}` : "guest");
const GUEST_SETTINGS_KEY = "abvme_guest_settings";

// Storage can be unavailable (private mode, blocked site data): never fail on it.
function storageGet(store, key) {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}
function storageSet(store, key, value) {
  try {
    if (value == null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    /* ignore */
  }
}

// ======================================================================
// Status bar (QStatusBar + QProgressBar)
// ======================================================================

const statusEl = () => $("#statusText");
let statusTimer = null;
let activeTasks = 0;

function setStatus(message, level = "info") {
  const el = statusEl();
  el.textContent = message;
  el.className = `status-text temp${level === "warning" ? " warn" : level === "error" ? " error" : ""}`;
  el.title = message;
  clearTimeout(statusTimer);
  if (level === "info") statusTimer = setTimeout(clearStatus, STATUS_TIMEOUT);
}

function clearStatus() {
  if (activeTasks > 0) {
    statusTimer = setTimeout(clearStatus, STATUS_TIMEOUT);
    return;
  }
  const el = statusEl();
  el.textContent = "Ready";
  el.className = "status-text";
  el.title = "";
}

function beginTask(message, { determinate = false } = {}) {
  activeTasks++;
  const bar = $("#progress");
  bar.classList.remove("d-none");
  bar.classList.toggle("busy", !determinate);
  bar.querySelector(".bar").style.width = determinate ? "0%" : "";
  bar.querySelector(".label").textContent = determinate ? "0/0" : "";
  setStatus(message);
  clearTimeout(statusTimer);
}

function updateProgress(current, total, message, label) {
  const bar = $("#progress");
  if (!total || total <= 1) {
    bar.classList.add("busy");
    bar.querySelector(".bar").style.width = "";
    bar.querySelector(".label").textContent = "";
  } else {
    bar.classList.remove("busy");
    bar.querySelector(".bar").style.width = `${Math.min(100, (current / total) * 100)}%`;
    bar.querySelector(".label").textContent = label ?? `${current}/${total}`;
  }
  if (message) {
    statusEl().textContent = message;
    statusEl().title = message;
  }
}

function endTask(message, level = "info") {
  activeTasks = Math.max(0, activeTasks - 1);
  if (!activeTasks) $("#progress").classList.add("d-none");
  if (message) setStatus(message, level);
  else if (!activeTasks) clearStatus();
}

// ======================================================================
// Settings (persisted per user on the server)
// ======================================================================

let pendingSettings = {};
const flushSettings = debounce(() => {
  const patch = pendingSettings;
  pendingSettings = {};
  if (guestMode()) {
    // Guests keep their UI options in this browser.
    const { last_workspace: _w, ...rest } = state.settings;
    storageSet(localStorage, GUEST_SETTINGS_KEY, JSON.stringify(rest));
  } else if (state.user) {
    api.put("/api/account/settings", patch).catch(() => {});
  }
}, 600);

function guestSettings() {
  try {
    return JSON.parse(storageGet(localStorage, GUEST_SETTINGS_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function setSetting(key, value) {
  state.settings[key] = value;
  pendingSettings[key] = value;
  flushSettings();
  syncOptionsMenu();
}

function syncOptionsMenu() {
  for (const el of $$("[data-toggle-setting]")) el.classList.toggle("checked", Boolean(state.settings[el.dataset.toggleSetting]));
  for (const ul of $$("[data-radio-setting]")) {
    const key = ul.dataset.radioSetting;
    for (const btn of ul.querySelectorAll("[data-value]")) btn.classList.toggle("checked", btn.dataset.value === state.settings[key]);
  }
}

function buildOptionsMenu() {
  for (const ul of $$("[data-radio-setting]")) {
    const key = ul.dataset.radioSetting;
    ul.innerHTML = OPTION_LABELS[key]
      .map(([label, value]) => `<li><button class="dropdown-item" data-value="${value}"><span class="check"></span><span class="label">${label}</span></button></li>`)
      .join("");
    ul.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-value]");
      if (btn) setSetting(key, btn.dataset.value);
    });
  }
  for (const el of $$("[data-toggle-setting]")) {
    el.addEventListener("click", () => {
      const key = el.dataset.toggleSetting;
      setSetting(key, !state.settings[key]);
      if (key === "show_all") applyAssetFilter(true);
    });
  }
  // Nested submenus: tap to expand on touch screens.
  for (const li of $$(".qmenu .submenu")) bindSubmenu(li);
  // A closed menu starts again with every submenu folded.
  $("#menubar").addEventListener("hidden.bs.dropdown", (e) => {
    for (const li of $$(".submenu.open", e.target.closest(".dropdown"))) li.classList.remove("open");
  });
  syncOptionsMenu();
}

// ======================================================================
// Login
// ======================================================================

let registerMode = false;

// Parts only some people may use: taken out of the page (setPresent), not
// just hidden. The server checks all of them on its own as well.
const gated = {
  firstUser: $("#loginFirstUser"),
  signup: $("#loginSwitch"),
  guest: $("#loginGuest"),
  toolsButton: $("#toolsButton"),
};
// Out until the page knows who is here (showLogin / enterApp put them back).
for (const el of Object.values(gated)) setPresent(el, false);

async function showLogin(message = "") {
  $("#appView").classList.add("d-none");
  $("#boot").classList.add("d-none");
  $("#loginView").classList.remove("d-none");
  setPasswordShown(false); // never left showing from an earlier visit
  for (const m of $$(".modal.show")) bootstrap.Modal.getInstance(m)?.hide();
  let config = { signup: true, first_user: false };
  try {
    config = await api.get("/api/auth/config");
  } catch {
    /* keep defaults */
  }
  setPresent(gated.firstUser, config.first_user);
  setPresent(gated.signup, config.signup);
  setRegisterMode(config.first_user);
  const err = $("#loginError");
  err.textContent = message;
  err.classList.toggle("d-none", !message);
  $("#loginUser").focus();
  const guest = await guestConfig();
  setPresent(gated.guest, Boolean(guest) && !config.first_user);
  if (guest) {
    gated.guest.querySelector("#loginGuestHint").textContent = `No account needed: open up to ${formatByteSize(guest.limits.open_bytes)} of files at a time, within a daily processing limit.`;
  }
}

/** Guest access settings, or null when guests are turned off. */
async function guestConfig() {
  try {
    return await api.get("/api/guest/config");
  } catch {
    return null;
  }
}

/** Show or hide the typed passwords on the sign-in page. */
function setPasswordShown(show) {
  for (const id of ["loginPass", "loginPass2"]) $(`#${id}`).type = show ? "text" : "password";
  for (const btn of $$("[data-pw-toggle]")) {
    btn.textContent = show ? "Hide" : "Show";
    const label = show ? "Hide password" : "Show password";
    btn.setAttribute("aria-label", label);
    btn.title = label;
  }
}

function setRegisterMode(on) {
  registerMode = on;
  $("#loginSubmit").textContent = on ? "Create account" : "Sign in";
  $("#loginConfirmRow").classList.toggle("d-none", !on);
  $("#loginRememberRow").classList.toggle("d-none", on);
  $("#loginPass").autocomplete = on ? "new-password" : "current-password";
  gated.signup.querySelector("#loginSwitchText").textContent = on ? "Already have an account?" : "No account?";
  gated.signup.querySelector("#loginSwitchLink").textContent = on ? "Sign in" : "Create one";
}

function bindLogin() {
  for (const btn of $$("[data-pw-toggle]")) {
    btn.addEventListener("click", () => setPasswordShown(btn.textContent !== "Hide"));
  }
  // A page restored from the back/forward cache keeps its state: hide again.
  window.addEventListener("pageshow", (e) => e.persisted && setPasswordShown(false));
  $("#loginGuestLink", gated.guest).addEventListener("click", async (e) => {
    e.preventDefault();
    const config = await guestConfig();
    if (config) startGuest(config);
    else showLogin("Guest access is not available right now.");
  });
  $("#loginSwitchLink", gated.signup).addEventListener("click", (e) => {
    e.preventDefault();
    setRegisterMode(!registerMode);
    $("#loginError").classList.add("d-none");
  });
  $("#loginForm").addEventListener("submit", async (e) => {
    e.preventDefault();
    const err = $("#loginError");
    err.classList.add("d-none");
    const username = $("#loginUser").value.trim();
    const password = $("#loginPass").value;
    if (registerMode && password !== $("#loginPass2").value) {
      err.textContent = "Passwords do not match";
      err.classList.remove("d-none");
      return;
    }
    const btn = $("#loginSubmit");
    btn.disabled = true;
    try {
      const path = registerMode ? "/api/auth/register" : "/api/auth/login";
      const { user } = await api.post(path, { username, password, remember: $("#loginRemember").checked });
      $("#loginPass").value = "";
      $("#loginPass2").value = "";
      await startApp(user);
    } catch (ex) {
      err.textContent = ex.message;
      err.classList.remove("d-none");
    } finally {
      btn.disabled = false;
    }
  });
}

// ======================================================================
// App start
// ======================================================================

async function boot() {
  preloadIcons(APP_ICONS);
  bindLogin();
  setWorkspaceExpiredHandler(markServerGone);
  let user = null;
  // Before any guest request: the quota is counted per browser + network.
  // Loaded on its own so a content blocker that stops it cannot stop the app
  // (the server then counts the network only).
  const fingerprint = import("./traits.js")
    .then((m) => m.browserFingerprint())
    .then(setGuestFingerprint)
    .catch(() => {});
  try {
    // { user: null } when nobody is signed in (no 401 in the console for guests).
    ({ user } = await api.get("/api/auth/session"));
    if (!user) {
      // Not signed in: use ABVME as a guest when that is allowed.
      await fingerprint;
      const config = await guestConfig();
      if (config) return startGuest(config);
      showLogin();
    }
  } catch (err) {
    showLogin(err.message);
  } finally {
    // Only after the first check: a guest's 401 there is expected, not an expiry.
    setUnauthorizedHandler((message) => showLogin(message || "Your session has expired. Please sign in again."));
  }
  if (user) await startApp(user);
}

let appBound = false;

/** Fold a submenu together with everything opened inside it. */
function closeSubmenu(li) {
  li.classList.remove("open");
  for (const inner of $$(".submenu.open", li)) inner.classList.remove("open");
}

/** Only one submenu per level stays open: fold the others next to this one. */
function closeSiblingSubmenus(li) {
  for (const other of li.parentElement.children) {
    if (other !== li && other.classList.contains("submenu")) closeSubmenu(other);
  }
}

/** Open or close a submenu in place, then let the menu fit the screen again (it may have grown wider). */
function toggleSubmenu(li) {
  if (li.classList.contains("open")) {
    closeSubmenu(li);
  } else {
    closeSiblingSubmenus(li);
    li.classList.add("open");
  }
  const toggle = $('[data-bs-toggle="dropdown"]', li.closest(".dropdown"));
  bootstrap.Dropdown.getInstance(toggle)?.update();
}

/** A tap on the item toggles the submenu; pointing at a submenu with a mouse folds the ones beside it. */
function bindSubmenu(li) {
  $(":scope > .dropdown-item", li).addEventListener("click", (e) => {
    e.stopPropagation();
    toggleSubmenu(li);
  });
  // Not for touch: the folding would move the item before the tap lands on it.
  li.addEventListener("pointerenter", (e) => {
    if (e.pointerType === "mouse") closeSiblingSubmenus(li);
  });
}

/**
 * Fit the menu bar to its width: first the account name goes (icon only),
 * then menus from the right move, as submenus, into the arrow menu (#menuMore).
 */
let barMenus = null;
function fitMenubar() {
  const bar = $("#menubar");
  const more = $("#menuMore");
  const right = $(".menubar-right", bar);
  const userBtn = $("#userMenuBtn");
  if (!barMenus) {
    barMenus = $$(":scope > .dropdown:not(#menuMore)", bar).map((div) => {
      const btn = $(":scope > .menu-btn", div);
      const li = document.createElement("li");
      li.className = "submenu";
      li.hidden = true;
      li.innerHTML = `<button class="dropdown-item"><span class="ico-space"></span><span class="label">${escapeHtml(btn.textContent)}</span></button>`;
      bindSubmenu(li);
      $("#menuMoreList").append(li);
      return { div, btn, ul: $(":scope > .dropdown-menu", div), li };
    });
  }
  // Close any open menu before its list moves.
  for (const t of $$('[data-bs-toggle="dropdown"]', bar)) bootstrap.Dropdown.getInstance(t)?.hide();
  for (const m of barMenus) {
    if (m.ul.parentElement !== m.div) m.div.append(m.ul);
    m.div.hidden = false;
    m.li.hidden = true;
    m.li.classList.remove("open");
  }
  more.hidden = true;
  userBtn.classList.remove("no-name");
  const fits = () => right.scrollWidth <= right.clientWidth;
  if (fits()) return;
  userBtn.classList.add("no-name");
  if (fits()) return;
  more.hidden = false;
  for (let i = barMenus.length - 1; i > 0 && !fits(); i--) {
    const m = barMenus[i];
    m.div.hidden = true;
    m.li.append(m.ul);
    m.li.hidden = false;
  }
}

function enterApp(name, isAdmin) {
  $("#loginView").classList.add("d-none");
  $("#boot").classList.add("d-none");
  $("#appView").classList.remove("d-none");
  document.body.classList.toggle("guest-mode", guestMode());
  $("#userName").textContent = name;
  const userBtn = $("#userMenuBtn");
  userBtn.classList.toggle("signed-in", !guestMode());
  userBtn.classList.toggle("admin", isAdmin);
  userBtn.title = guestMode() ? "Guest (not signed in)" : `Signed in as ${name}${isAdmin ? " (admin)" : ""}`;
  if (!appBound) {
    bindApp();
    appBound = true;
  }
  setPresent(gated.toolsButton, !guestMode());
  fitMenubar();
  syncOptionsMenu();
  applySplit();
  resetWorkspaceView();
  renderRecentMenu();
  refreshBackendStatus();
}

async function startApp(user) {
  state.user = user;
  state.guest = null;
  state.settings = { ...DEFAULT_SETTINGS, ...(user.settings || {}) };
  enterApp(user.username, user.role === "admin");
  await restoreTab();
}

async function startGuest(config) {
  state.user = null;
  state.guest = config;
  state.settings = { ...DEFAULT_SETTINGS, ...guestSettings() };
  enterApp("Guest", false);
  updateQuotaBadge(config.quota);
  await restoreTab();
}

/** Status bar badge: share of today's processing quota left. */
function updateQuotaBadge(quota = state.status?.quota) {
  const badge = $("#quotaBadge");
  if (!quota) return;
  badge.classList.toggle("d-none", Boolean(quota.unlimited));
  if (quota.unlimited) return;
  badge.textContent = fmtPercent(quota.percent);
  badge.title = `Processing quota left today: ${fmtPercent(quota.percent)}. Resets ${fmtDate(quota.resets_at)}.`;
  badge.classList.toggle("low", !quota.exhausted && quota.percent <= 20);
  badge.classList.toggle("out", Boolean(quota.exhausted));
}

/** Quota percentages always show one decimal (the Worker rounds them half up). */
const fmtPercent = (p) => `${Number(p ?? 0).toFixed(1)}%`;

const quotaOut = () => {
  const q = state.status?.quota ?? state.guest?.quota;
  return Boolean(q && !q.unlimited && q.exhausted);
};

// ----- guest verification (Cloudflare Turnstile) --------------------------

let turnstileScript = null;
function loadTurnstile() {
  turnstileScript ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      turnstileScript = null;
      script.remove();
      reject(new Error("Could not load the verification check. Check your connection or content blocker, then try again."));
    };
    document.head.appendChild(script);
  });
  return turnstileScript;
}

/**
 * Run the Turnstile check (Managed, interaction-only): it works in the background,
 * and a popup with just the widget appears only if Cloudflare wants a click, or
 * when the check fails. Resolves with the token, or null if the user cancels.
 */
function turnstileToken(siteKey) {
  const popup = $("#verifyPopup");
  const box = $("#turnstileBox");
  const errEl = $("#verifyError");
  errEl.classList.add("d-none");
  box.innerHTML = "";
  return new Promise((resolve) => {
    let widget = null;
    let settled = false;
    const show = () => {
      popup.classList.add("show");
      popup.setAttribute("aria-hidden", "false");
    };
    const showErr = (text) => {
      errEl.textContent = text;
      errEl.classList.remove("d-none");
      show();
    };
    const onKey = (e) => e.key === "Escape" && popup.classList.contains("show") && finish(null);
    const finish = (token) => {
      if (settled) return;
      settled = true;
      popup.classList.remove("show");
      popup.setAttribute("aria-hidden", "true");
      document.removeEventListener("keydown", onKey);
      if (widget !== null) {
        try {
          window.turnstile.remove(widget);
        } catch {
          /* already gone */
        }
      }
      box.innerHTML = "";
      endTask();
      resolve(token);
    };
    $("#verifyCancel").onclick = () => finish(null);
    document.addEventListener("keydown", onKey);
    beginTask("Checking your browser...");
    loadTurnstile()
      .then((turnstile) => {
        widget = turnstile.render(box, {
          sitekey: siteKey,
          theme: "dark",
          appearance: "interaction-only",
          callback: (token) => finish(token),
          "before-interactive-callback": show,
          "error-callback": () => showErr("Verification failed. Please try again."),
          "timeout-callback": () => {
            showErr("Verification timed out. Please try again.");
            turnstile.reset(widget);
          },
          "unsupported-callback": () => showErr("This browser cannot run the verification check."),
          "expired-callback": () => turnstile.reset(widget),
        });
      })
      .catch((err) => showErr(err.message));
  });
}

/** Make sure this browser has a guest device id; asks for a Turnstile check once. */
async function ensureGuestVerified() {
  if (state.guest.verified) return true;
  let token = "";
  if (state.guest.turnstile_site_key) {
    token = await turnstileToken(state.guest.turnstile_site_key);
    if (!token) return false;
  }
  try {
    const { quota } = await api.post("/api/guest/start", { token });
    state.guest.verified = true;
    state.guest.quota = quota;
    updateQuotaBadge(quota);
    return true;
  } catch (err) {
    showError("Verification", err.message);
    return false;
  }
}

let atlasToolkit;
let gameFiles;

/** One asset as exported by the server (the raw file), or null when cancelled. */
async function exportFile(asset, purpose = "Atlas Toolkit") {
  const resp = await withServer([asset.file_id], purpose, () =>
    request("POST", `${apiBase()}/workspaces/${state.workspace.id}/export`, {
      json: { items: [{ file_id: asset.file_id, path_ids: [asset.path_id] }] },
      raw: true,
    }),
  );
  if (resp === CANCELLED) return null;
  const name = decodeURIComponent(resp.headers.get("x-abvme-filename") || "export.bin");
  return new File([await resp.blob()], name);
}

function bindApp() {
  new ResizeObserver(fitMenubar).observe($("#menubar"));
  atlasToolkit = createAtlasToolkit({
    assets: () => state.assets,
    atlasText: async (asset) => {
      return previewText(await assetItem(asset));
    },
    fileName: (asset) => state.files.get(asset.file_id)?.name ?? "",
    localItem: assetItem,
    exportFile,
    version: assetSig,
    replace: (asset, file) => doReplace(asset, file, null),
    beginTask,
    endTask,
    showError,
    showWarning,
    openModal,
    closeModal,
  });
  gameFiles = createGameFiles({
    search: (server, platform, pattern, method, matchCase) => api.post(`${apiBase()}/gamefiles/search`, { server, platform, pattern, method, matchCase }),
    catalog: () => api.get(`${apiBase()}/gamefiles/catalog`),
    maxFiles: () => state.status?.limits?.game_max_files ?? 20,
    start: openGameFiles,
    openModal,
    closeModal,
  });
  table = new AssetTable($("#assetTable"), {
    onSelectionChange: onSelectionChanged,
    onActivate: () => {
      if (isMobile() && !state.selectMode && state.selected.length === 1) setMobileView("preview");
    },
    onLongPress: (index) => {
      if (!isMobile()) return;
      setSelectMode(true);
      table._toggle(index);
    },
    onFiltersChanged: () => {
      const n = table?.activeFilterCount() ?? 0;
      const badge = $("#mFilterCount");
      badge.textContent = String(n);
      badge.classList.toggle("d-none", !n);
      if (state.workspace) updateActions();
    },
  });
  const syncLayout = () => {
    table.setCompact(isMobile());
    if (!isMobile() && state.selectMode) setSelectMode(false);
  };
  matchMedia("(max-width: 767.98px)").addEventListener("change", syncLayout);
  syncLayout();
  bindPhoneListbar();
  viewer = new PhotoViewer($("#photoViewer"), {
    onContextMenu: (e) =>
      contextMenu(e.clientX, e.clientY, [
        { label: "Copy image", disabled: !viewer.hasPhoto, action: () => copyPreviewImage() },
        localDownload()
          ? { label: "Download image", disabled: !viewer.hasPhoto, action: () => downloadPreviewPng() }
          : // Reduced preview, or the export is not a PNG: get the file from the server.
            { label: previewScaled() ? "Download full image (Export)" : "Download image (Export)", disabled: !viewer.hasPhoto, action: () => onExportClicked() },
        "-",
        { label: "Fit to view", disabled: !viewer.hasPhoto, action: () => viewer.resetView() },
      ]),
  });
  overlay = new DropOverlay($("#dropOverlay"));
  buildOptionsMenu();
  bindRecentMenu();

  document.addEventListener("click", (e) => {
    const el = e.target.closest("[data-action]");
    if (!el || el.disabled || el.classList.contains("disabled")) return;
    if (el.tagName === "A") e.preventDefault();
    runAction(el.dataset.action);
  });

  // Preview tabs
  for (const tabs of $$(".qtabs")) {
    tabs.querySelector(".tabbar").addEventListener("click", (e) => {
      const tab = e.target.closest(".tab");
      if (!tab) return;
      for (const t of tabs.querySelectorAll(".tab")) t.classList.toggle("active", t === tab);
      for (const p of tabs.querySelectorAll(":scope > .pane")) p.classList.toggle("active", p.dataset.pane === tab.dataset.tab);
      if (tab.dataset.tab === "dump") fillDump();
    });
  }

  // Desktop pickers filter to .bundle / .unity3d (All Files is still one click away).
  // Phone pickers have no such switch and many bundles have no extension, so they
  // list every file; non-UnityFS files are skipped when opening either way.
  if (matchMedia("(pointer: coarse)").matches) $("#openFilesInput").removeAttribute("accept");
  $("#openFilesInput").addEventListener("change", (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) openFiles(files);
  });
  $("#openFolderInput").addEventListener("change", (e) => {
    const all = Array.from(e.target.files || []);
    e.target.value = "";
    onFolderPicked(all);
  });
  $("#replaceInput").addEventListener("change", (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const asset = singleSelected();
    if (file && asset) startReplace(asset, file);
  });

  bindSplitter();
  bindDragDrop();
  bindShortcuts();
  bindSaveDialog();
  bindTextureDialog();
  bindAccountDialogs();
  bindKeepAlive();
  $("#backendDot").addEventListener("click", () => runAction("status"));
  // The status (quota, service, announcement) is fetched again after every
  // action; on its own only every few minutes while the tab is in view.
  setInterval(() => document.visibilityState === "visible" && Date.now() - lastStatusAt >= STATUS_POLL_MS && refreshBackendStatus(), 30_000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && Date.now() - lastStatusAt >= STATUS_POLL_MS) refreshBackendStatus();
  });
  window.addEventListener("beforeunload", (e) => {
    if (uploadInProgress) {
      e.preventDefault();
      e.returnValue = "";
    }
  });
}

// ======================================================================
// Actions / enablement
// ======================================================================

function singleSelected() {
  return state.selected.length === 1 ? state.selected[0] : null;
}

function updateActions() {
  const one = singleSelected();
  const needs = {
    assets: state.assets.length > 0,
    workspace: Boolean(state.workspace),
    selection: state.selected.length > 0,
    // Export works for some types only (Texture2D, TextAsset, Mesh).
    exportable: state.selected.some((a) => a.exportable),
    editable: Boolean(one?.editable),
    changed: state.selected.some((a) => a.changed),
    // Tools are for signed-in users (guests see the menu with the items disabled).
    tools: Boolean(state.user) && state.assets.length > 0,
    // Game Files needs no open file; the administrator can switch it off.
    user: Boolean(state.user) && state.status?.game_files !== false,
  };
  for (const el of $$("[data-needs]")) {
    const ok = Boolean(needs[el.dataset.needs]) && !state.busy;
    if (el.classList.contains("dropdown-item")) el.classList.toggle("disabled", !ok);
    else el.disabled = !ok;
  }
  $("#recentMenu > .dropdown-item").classList.toggle("disabled", Boolean(state.busy));
  for (const el of $$('[data-action="open-files"], [data-action="open-folder"]')) {
    if (el.classList.contains("dropdown-item")) el.classList.toggle("disabled", Boolean(state.busy));
    else el.disabled = Boolean(state.busy);
  }
  $("#mSelectAllBtn").textContent = allListedSelected() ? "Deselect All" : "Select All";
  const footer = $("#tableFooter");
  if (!state.workspace) footer.innerHTML = "";
  else {
    const changed = state.assets.filter((a) => a.changed).length;
    footer.innerHTML = `<span>${table.view.length.toLocaleString()} of ${visibleAssets().length.toLocaleString()} assets</span>${
      state.selected.length ? `<span>· ${state.selected.length.toLocaleString()} selected</span>` : ""
    }${changed ? `<span style="color:var(--abv-changed)">· ${changed} changed</span>` : ""}<span class="spacer"></span><span>${state.files.size} file(s)</span>`;
  }
}

const allListedSelected = () => table.view.length > 0 && table.view.every((a) => table.selected.has(a.key));

async function runAction(action) {
  closeFilterMenu();
  switch (action) {
    case "open-files":
      if (!state.busy) onOpenFilesClicked();
      break;
    case "open-folder":
      if (!state.busy) onOpenFolderClicked();
      break;
    case "save":
      if (state.assets.length && !state.busy) openSaveDialog();
      break;
    case "replace":
      onReplaceClicked();
      break;
    case "export":
      onExportClicked();
      break;
    case "atlas-toolkit":
      if (!state.busy && state.user) atlasToolkit.pick(singleSelected());
      break;
    case "game-files":
      if (!state.busy && state.user) gameFiles.show();
      break;
    case "revert":
      revertSelected();
      break;
    case "select-all":
      table.selectAll();
      break;
    case "close-workspace":
      if (await confirmDiscard("Close Workspace", "Closing the workspace")) closeWorkspace();
      break;
    case "sign-in":
      showLogin();
      break;
    case "about":
      openAbout();
      break;
    case "account":
      if (!guestMode()) openAccountDialog();
      break;
    case "status":
      openStatusDialog();
      break;
    case "logout":
      if (!(await confirmDiscard("Sign Out", "Signing out"))) break;
      // The tab's work belongs to this account: drop it on both sides.
      await closeWorkspace();
      await api.post("/api/auth/logout").catch(() => {});
      state.user = null;
      showLogin();
      break;
    case "back-to-list":
      setMobileView("list");
      break;
    case "toggle-select-mode":
      // Leaving select mode drops what was picked in it.
      if (state.selectMode) table.clearSelection();
      setSelectMode(!state.selectMode);
      break;
    case "m-toggle-all":
      // One button: Deselect All once everything listed is selected, else Select All.
      if (allListedSelected()) table.clearSelection();
      else {
        setSelectMode(true);
        table.selectAll();
      }
      break;
    default:
      break;
  }
}

function setMobileView(view) {
  $("#appView").dataset.mobileView = view;
  if (view === "preview") requestAnimationFrame(() => viewer.resetView());
}

function setSelectMode(on) {
  state.selectMode = on;
  table.setSelectMode(on);
  $("#appView").classList.toggle("select-mode", on);
  const btn = $("#selectModeBtn");
  btn.classList.toggle("btn-primary-abv", on);
  btn.setAttribute("aria-pressed", String(on));
}

// ----- phone list bar: search, filter, sort --------------------------------

const COLUMN_LABELS = { name: "Name", type: "Type", path_id: "PathID", container: "Container", size: "Size", source: "Source file" };

function bindPhoneListbar() {
  const search = $("#mSearch");
  const apply = debounce(() => table.setQuickSearch(search.value), 150);
  search.addEventListener("input", apply);
  search.addEventListener("search", apply);

  $("#mFilterBtn").addEventListener("click", (e) => {
    // Typed just now (the search waits 150 ms): count it before the menu reads
    // the filters, or "Clear all filters" is off and the badge shows up later.
    table.setQuickSearch(search.value);
    const btn = e.currentTarget;
    const r = btn.getBoundingClientRect();
    const order = ["type", "source", "name", "container", "path_id"];
    contextMenu(r.left, r.bottom + 2, [
      ...order.map((col) => ({
        label: `${table.hasFilter(col) ? "● " : ""}Filter by ${COLUMN_LABELS[col]}…`,
        action: () => table.openFilterMenu(col, btn),
      })),
      "-",
      {
        label: "Clear all filters",
        disabled: !table.activeFilterCount(),
        action: () => {
          search.value = "";
          table.quick = "";
          table.clearFilters();
        },
      },
    ]);
  });

  $("#mSortBtn").addEventListener("click", (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const { col, dir } = table.sort;
    contextMenu(r.left, r.bottom + 2, [
      ...COLUMNS.map((c) => ({
        label: `${c.id === col ? "✓ " : "\u2003"}${COLUMN_LABELS[c.id]}`,
        action: () => table.setSort(c.id, c.id === col ? dir : 1),
      })),
      "-",
      { label: `${dir > 0 ? "✓ " : "\u2003"}Ascending`, action: () => table.setSort(col, 1) },
      { label: `${dir < 0 ? "✓ " : "\u2003"}Descending`, action: () => table.setSort(col, -1) },
    ]);
  });
}

// ======================================================================
// Splitter
// ======================================================================

function applySplit() {
  const frac = Number(state.settings.left_width);
  const left = $("#leftPanel");
  if (frac > 0.1 && frac < 0.9) left.style.setProperty("--left-width", `${frac * 100}%`);
  else left.style.removeProperty("--left-width");
}

function bindSplitter() {
  const splitter = $("#splitter");
  const main = $("#mainArea");
  splitter.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    splitter.setPointerCapture(e.pointerId);
    splitter.classList.add("dragging");
    const rect = main.getBoundingClientRect();
    const move = (ev) => {
      const x = Math.min(Math.max(ev.clientX - rect.left - 9, 350), rect.width - 240);
      $("#leftPanel").style.setProperty("--left-width", `${x}px`);
    };
    const up = () => {
      splitter.classList.remove("dragging");
      splitter.removeEventListener("pointermove", move);
      splitter.removeEventListener("pointerup", up);
      const w = $("#leftPanel").getBoundingClientRect().width;
      setSetting("left_width", Math.round((w / rect.width) * 1000) / 1000);
      applySplit();
      viewer.zoom === 0 && viewer.resetView();
    };
    splitter.addEventListener("pointermove", move);
    splitter.addEventListener("pointerup", up);
  });
  splitter.addEventListener("dblclick", () => {
    setSetting("left_width", null);
    applySplit();
  });
}

// ======================================================================
// Workspace loading (Open Files / Open Folder)
// ======================================================================

let uploadInProgress = false;

function resetWorkspaceView() {
  state.workspace = null;
  state.serverGone = false;
  state.serverReady = new Set();
  sourceFiles.clear();
  state.files = new Map();
  state.edits = new Map();
  state.savedEdits = new Set();
  state.assets = [];
  state.selected = [];
  state.itemCache.clear();
  $("#workspaceName").textContent = "";
  document.title = "ABVME";
  $("#mSearch").value = "";
  table.quick = "";
  if (state.selectMode) setSelectMode(false);
  table.clear();
  const g = guestMode() ? state.guest.limits : null;
  const guestHint = g
    ? `<div class="hint guest-hint">Guest mode · up to ${formatByteSize(g.open_bytes)} per Open · <a href="#" data-action="status">Details</a> · <a href="#" data-action="sign-in">Sign in</a></div>`
    : "";
  table.setEmptyMessage(
    `<div><div>Open UnityFS asset bundles (<code>*.bundle</code>, <code>*.unity3d</code>, <code>*.ab</code>, ...) to get started.</div><div class="hint">Use <b>Open Files</b>, File &gt; Open Folder, or drop the files anywhere.</div>${guestHint}<div class="hint support-hint"><img class="ico" src="/icons/heart-solid.svg" alt="" />Help cover server costs: <a href="https://ko-fi.com/com55" target="_blank" rel="noopener">Ko-fi</a></div></div>`,
  );
  showPlaceholder("Select an asset from the list to view its preview.");
  updateActions();
}

async function onOpenFilesClicked() {
  const coarse = matchMedia("(pointer: coarse)").matches;
  const files = await pickFiles({ multiple: true, accept: coarse ? null : { description: "Asset bundles", extensions: OPEN_SUFFIXES } });
  if (files === null) $("#openFilesInput").click();
  else if (files.length && !state.busy) openFiles(files);
}

async function onOpenFolderClicked() {
  const files = await pickFolderFiles();
  if (files === null) $("#openFolderInput").click();
  else if (files.length && !state.busy) onFolderPicked(files);
}

async function onFolderPicked(all) {
  // Only top-level bundles, like the desktop "Open Folder".
  const files = all.filter((f) => {
    const rel = f.webkitRelativePath || f.name;
    return rel.split("/").length <= 2 && OPEN_SUFFIXES.includes(suffix(f.name));
  });
  if (!files.length) {
    setStatus("No asset bundles were found in the selected folder.", "warning");
    return;
  }
  if (files.length > OPEN_FOLDER_CONFIRM_AFTER) {
    const ok = await askYesNo("Open Folder", `The selected folder contains ${files.length} asset bundles.\n\nLoad them all?`);
    if (!ok) return;
  }
  openFiles(files);
}

// ----- File > Open Recent ----------------------------------------------------
// The list lives in this browser only (localstore.js), per signed-in user or
// guest. Where the browser gives file handles (Chromium on a computer) an entry
// reads its files again from disk and may ask for permission once. Everywhere
// else (phones, Firefox, Safari) the files are copied into the browser's
// storage, within a size limit, so they open again without picking them.

/** The entries the menu shows now; looked up in a click without waiting. */
let recentEntries = [];

async function renderRecentMenu() {
  const ul = $("#recentList");
  recentEntries = await local.listRecent(ownerKey()).catch(() => []);
  if (!recentEntries.length) {
    ul.innerHTML = `<li><div class="menu-note">No recent files</div></li>`;
    return;
  }
  const copies = recentEntries.reduce((n, r) => n + local.recentCopyBytes(r), 0);
  ul.innerHTML =
    recentEntries
      .map((r) => {
        const names = r.files.map((f) => f.name);
        const label = names.length === 1 ? names[0] : `${names.length} files: ${names.join(", ")}`;
        return `<li><button class="dropdown-item" data-recent="${escapeHtml(r.id)}" title="${escapeHtml(names.join("\n"))}"><span class="label">${escapeHtml(label)}</span><span class="shortcut">${escapeHtml(relTime(r.opened_at))}</span></button></li>`;
      })
      .join("") +
    `<li><hr class="dropdown-divider" /></li>${copies ? `<li><div class="menu-note">Copies kept on this device: ${formatByteSize(copies)}</div></li>` : ""}<li><button class="dropdown-item" data-recent-clear><span class="label">Clear Recent</span></button></li>`;
}

function bindRecentMenu() {
  $("#recentList").addEventListener("click", (e) => {
    if (e.target.closest("[data-recent-clear]")) {
      local.clearRecent(ownerKey()).catch(() => {}).then(renderRecentMenu);
      return;
    }
    const id = e.target.closest("[data-recent]")?.dataset.recent;
    if (id) openRecent(id);
  });
  // Another tab may have opened files since this menu was drawn.
  $("#recentMenu").closest(".dropdown").addEventListener("show.bs.dropdown", renderRecentMenu);
}

async function openRecent(id) {
  const entry = recentEntries.find((r) => r.id === id);
  if (!entry || state.busy) return;
  const { files, missing } = await filesFromRecent(entry);
  if (!files.length) {
    await showWarning("Open Recent", "These files could not be read. They may have been moved or deleted, or access was refused.", missing.join("\n"));
    return;
  }
  if (missing.length && !(await askYesNo("Open Recent", `${missing.length} of ${entry.files.length} file(s) could not be read:\n\n${missing.join("\n")}\n\nOpen the others?`))) return;
  openFiles(files);
}

/** Add the files just opened to the list. */
async function rememberRecent(files) {
  try {
    const again = files[0].recentId;
    if (again && files.every((f) => f.recentId === again)) {
      await local.touchRecent(again);
      return renderRecentMenu();
    }
    const folder = files.every((f) => f.folder && f.folder === files[0].folder) ? files[0].folder : null;
    // Without disk storage a copy would only fill memory, and be lost on reload.
    const copy = await local.persistent();
    const kept = [];
    for (const f of files) {
      const item = { name: f.name, size: f.size };
      if (folder) kept.push(item);
      else if (f.handle) kept.push({ ...item, handle: f.handle });
      else if (copy) kept.push({ ...item, blob: f });
      else return;
    }
    // Asks the browser not to evict the copies when the device runs low on space.
    if (kept.some((k) => k.blob)) navigator.storage?.persist?.().catch(() => {});
    if (await local.addRecent(ownerKey(), { files: kept, folder })) await renderRecentMenu();
  } catch {
    // The list is a convenience: opening goes on without it.
  }
}

// ----- the tab's workspace ---------------------------------------------------
// Work is kept in this tab (localstore.js): the server only holds the files
// while the tab is in use and drops them after an hour idle. The tab keeps
// showing its work; actions that need the server send the files again first
// (see "the server's copy" below).

/** The record kept for this tab. */
function workspaceRecord(extra = {}) {
  return {
    tab: state.tab,
    owner: ownerKey(),
    ws: state.workspace,
    files: [...state.files.values()],
    edits: [...state.edits.values()],
    saved: [...state.savedEdits],
    ready: true,
    ...extra,
  };
}

const saveRecord = (extra) => local.saveWorkspaceRecord(workspaceRecord(extra)).catch(() => {});

/** Show this tab's workspace again after a reload. */
async function restoreTab() {
  await local.sweepIdle(state.tab).catch(() => {});
  let rec = null;
  try {
    rec = await local.loadWorkspaceRecord(state.tab);
  } catch {
    rec = null;
  }
  if (!rec?.ws) return;
  if (rec.owner !== ownerKey()) {
    await local.clearTab(state.tab).catch(() => {});
    return;
  }
  if (!rec.ready) {
    // Reloaded while the files were loading: pick the job up again if it is still there.
    try {
      if (!rec.job) throw new Error("Loading did not finish");
      await api.post(`${apiBase()}/workspaces/${rec.ws.id}/touch`);
      state.workspace = rec.ws;
      state.files = new Map(rec.files.map((f) => [f.id, f]));
      await finishOpen(rec.job, []);
    } catch {
      await local.clearTab(state.tab).catch(() => {});
      resetWorkspaceView();
    }
    return;
  }
  await showWorkspace(rec, { quiet: true });
  const ready = rec.files.filter((f) => f.status === "ready").map((f) => f.id);
  if (Date.now() - (rec.last_active ?? 0) > local.IDLE_MS) {
    markServerGone();
    return;
  }
  try {
    await api.post(`${apiBase()}/workspaces/${rec.ws.id}/touch`);
    state.serverReady = new Set(ready);
  } catch {
    markServerGone();
  }
}

/** Replacements made (or made again) since the last save that included their file. */
const unsavedEdits = () => [...state.edits].filter(([key, e]) => !state.savedEdits.has(`${key}@${e.created_at}`)).length;

/** Ask before dropping the open work when replacements have not been saved; true to go on. */
async function confirmDiscard(title, action) {
  const n = unsavedEdits();
  if (!n) return true;
  return askYesNo(title, `${n} replaced asset(s) have not been saved.\n\n${action} drops them. Continue?`);
}

async function closeWorkspace() {
  const id = state.workspace?.id;
  if (id && !state.serverGone) api.del(`${apiBase()}/workspaces/${id}`).catch(() => {});
  await local.clearTab(state.tab).catch(() => {});
  resetWorkspaceView();
  // A warning or error about the closed work would stay until replaced.
  if (id) setStatus("Workspace closed");
}

// Keep the server's copy alive while the tab is used (previews need no requests).
// After an hour unused the server has dropped it; the next action sends it again.
let lastActivity = Date.now();
let lastKeepAlive = Date.now();
function bindKeepAlive() {
  const onActivity = () => {
    const t = Date.now();
    if (state.workspace && t - lastActivity > local.IDLE_MS) markServerGone();
    lastActivity = t;
    if (!state.workspace || state.serverGone || state.busy || t - lastKeepAlive < KEEPALIVE_MS) return;
    lastKeepAlive = t;
    local.touchWorkspaceRecord(state.tab).catch(() => {});
    api.post(`${apiBase()}/workspaces/${state.workspace.id}/touch`).catch(() => {});
  };
  for (const type of ["pointerdown", "keydown", "wheel", "touchstart"]) window.addEventListener(type, onActivity, { passive: true, capture: true });
  document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && onActivity());
}

async function openFiles(fileList) {
  if (state.busy) return;
  const checks = await Promise.all(fileList.map(hasUnityFsHeader));
  const files = fileList.filter((_, i) => checks[i]);
  const skipped = fileList.filter((_, i) => !checks[i]);
  if (!files.length) {
    showWarning("Open Files", "None of the selected files is a UnityFS asset bundle.", skipped.map((f) => f.name).join("\n"));
    return;
  }
  const total = files.reduce((n, f) => n + f.size, 0);
  const more = guestMode() ? "\nSign in for more." : "";
  if (guestMode() && total > state.guest.limits.open_bytes) {
    showWarning("Guest limit", `Guests can open up to ${formatByteSize(state.guest.limits.open_bytes)} at a time; these files are ${formatByteSize(total)}.${more}`);
    return;
  }
  if (quotaOut()) {
    const q = state.status?.quota ?? state.guest?.quota;
    showWarning("Quota", `Today's processing quota is used up. It resets at ${fmtDate(q.resets_at)}.${more}`);
    return;
  }
  if (state.workspace && !(await confirmDiscard("Open Files", "Opening other files replaces the current workspace and"))) return;
  if (guestMode() && !(await ensureGuestVerified())) return;

  // A tab holds one workspace: opening again replaces (deletes) the previous one.
  const previous = state.workspace?.id ?? (await local.loadWorkspaceRecord(state.tab).catch(() => null))?.ws?.id;
  state.busy++;
  uploadInProgress = true;
  updateActions();
  await local.clearTab(state.tab).catch(() => {});
  resetWorkspaceView();
  const showUploaded = (ended) => table.setEmptyMessage(`<div><div class="spin mx-auto mb-2"></div>Uploading ${ended}/${files.length}…</div>`);
  showUploaded(0);
  beginTask(`Uploading 0/${files.length}`, { determinate: true });
  let handedOver = false;
  try {
    const list = files.map((f) => ({ name: f.name, size: f.size }));
    const name = workspaceName(files.map((f) => f.name));
    const create = () => api.post(`${apiBase()}/workspaces`, { name, files: list, replace: previous });
    let created;
    try {
      created = await create();
    } catch (err) {
      if (err.status === 429) refreshBackendStatus();
      if (err.status !== 428 || !guestMode()) throw err;
      // The device cookie was cleared: verify again.
      state.guest.verified = false;
      if (!(await ensureGuestVerified())) throw new Error("Verification was cancelled");
      created = await create();
    }
    const wsId = created.workspace.id;
    state.workspace = created.workspace;
    state.partSize = created.part_size;
    // Kept to send the files again if the server's copy expires (see ensureServer).
    created.files.forEach((meta, i) => {
      sourceFiles.set(meta.id, files[i]);
      if (files[i].handle) local.putHandle(state.tab, meta.id, files[i].handle).catch(() => {});
    });
    state.files = new Map(created.files.map((f) => [f.id, { id: f.id, name: f.name, size: f.size, status: "pending", asset_count: 0, error: null }]));
    $("#workspaceName").textContent = created.workspace.name;
    await saveRecord({ ready: false });

    let shown = 0;
    const failed = await uploadFiles(
      `${apiBase()}/workspaces/${wsId}`,
      created.files.map((meta, i) => ({ id: meta.id, file: files[i] })),
      created.part_size,
      (sent, ended) => {
        updateProgress(sent, total, `Uploading ${ended}/${files.length}`, `${Math.floor((sent / total) * 100)}%`);
        if (ended !== shown) showUploaded((shown = ended));
      },
    );
    uploadInProgress = false;
    const names = new Map(created.files.map((meta, i) => [meta.id, files[i].name]));
    const failures = failed.map((f) => `${names.get(f.id)}: ${f.error.message}`);
    if (failures.length === files.length) throw new Error(`Upload failed.\n${failures.join("\n")}`);
    rememberRecent(files);

    updateProgress(0, 0, "Preparing previews...");
    table.setEmptyMessage(`<div><div class="spin mx-auto mb-2"></div>Loading asset bundles…</div>`);
    const { job } = await api.post(`${apiBase()}/workspaces/${wsId}/open`);
    await saveRecord({ ready: false, job });
    endTask();
    state.busy = Math.max(0, state.busy - 1);
    handedOver = true;
    await finishOpen(job, [...failures, ...skipped.map((f) => `${f.name}: not a UnityFS asset bundle`)]);
  } catch (err) {
    uploadInProgress = false;
    if (!handedOver) {
      state.busy = Math.max(0, state.busy - 1);
      endTask(`Failed to open files: ${err.message}`, "error");
    }
    table.setEmptyMessage(`<div>Failed to open files.</div>`);
    showError("Open Files", err.message);
    updateActions();
  }
}

/**
 * Tools > Game Files: the server fetches the chosen bundles from the game into a
 * new workspace (replacing this tab's), then they are opened like uploaded files.
 */
async function openGameFiles({ server, platform, files, pattern }) {
  if (state.busy) return;
  if (quotaOut()) {
    const q = state.status?.quota;
    showWarning("Quota", `Today's processing quota is used up. It resets at ${fmtDate(q.resets_at)}.`);
    return;
  }
  if (state.workspace && !(await confirmDiscard("Game Files", "Fetching game files replaces the current workspace and"))) return;
  const previous = state.workspace?.id ?? (await local.loadWorkspaceRecord(state.tab).catch(() => null))?.ws?.id;
  state.busy++;
  updateActions();
  await local.clearTab(state.tab).catch(() => {});
  resetWorkspaceView();
  table.setEmptyMessage(`<div><div class="spin mx-auto mb-2"></div>Fetching ${files.length} file(s) from the game…</div>`);
  beginTask(`Fetching ${files.length} file(s) from the game...`, { determinate: true });
  let handedOver = false;
  try {
    const list = files.map((f) => ({ ref: f.ref, name: f.name, size: f.size ?? 0 }));
    const name = workspaceName(files.map((f) => f.name), { game: true, label: pattern });
    const created = await api.post(`${apiBase()}/gamefiles/open`, { server, platform, files: list, name, replace: previous });
    const wsId = created.workspace.id;
    state.workspace = created.workspace;
    state.partSize = created.part_size;
    state.files = new Map(created.files.map((f) => [f.id, { id: f.id, name: f.name, size: f.size, status: "pending", asset_count: 0, error: null, game: f.game }]));
    $("#workspaceName").textContent = created.workspace.name;
    await saveRecord({ ready: false, job: created.job });

    const fetched = await pollJob(created.job, (j) => updateProgress(j.progress, j.total, j.message && j.message !== "Done" ? j.message : null, j.total ? `${Math.floor((j.progress / j.total) * 100)}%` : undefined));
    refreshBackendStatus();
    if (fetched.status === "error") throw new Error(fetched.error || "Fetching failed");
    // The sizes are only known now; files the game would not give are marked.
    const info = await api.get(`${apiBase()}/workspaces/${wsId}`);
    const failures = [];
    for (const f of info.files) {
      const meta = state.files.get(f.id);
      if (!meta) continue;
      meta.size = f.size;
      if (f.status === "error") {
        Object.assign(meta, { status: "error", error: f.error });
        failures.push(`${f.name}: ${f.error}`);
      }
    }
    if (failures.length === state.files.size) throw new Error(`The files could not be fetched.\n${failures.join("\n")}`);

    updateProgress(0, 0, "Preparing previews...");
    table.setEmptyMessage(`<div><div class="spin mx-auto mb-2"></div>Loading asset bundles…</div>`);
    const { job } = await api.post(`${apiBase()}/workspaces/${wsId}/open`);
    await saveRecord({ ready: false, job });
    endTask();
    state.busy = Math.max(0, state.busy - 1);
    handedOver = true;
    await finishOpen(job, failures);
  } catch (err) {
    if (!handedOver) {
      state.busy = Math.max(0, state.busy - 1);
      endTask(`Failed to fetch game files: ${err.message}`, "error");
    }
    table.setEmptyMessage(`<div>Failed to fetch game files.</div>`);
    showError("Game Files", err.message);
    updateActions();
  }
}

/**
 * Wait for the open job, then download each file's pack (asset list, previews,
 * dumps) into this tab. After this, viewing needs no server.
 */
async function finishOpen(job, extraErrors) {
  state.busy++;
  updateActions();
  beginTask("Preparing previews...", { determinate: true });
  try {
    table.setEmptyMessage(`<div><div class="spin mx-auto mb-2"></div>Loading asset bundles…</div>`);
    const done = await pollJob(job, (j) => updateProgress(j.progress, j.total, j.message && j.message !== "Done" ? j.message : null, j.total ? `${Math.floor((j.progress / j.total) * 100)}%` : undefined));
    refreshBackendStatus();
    if (done.status === "error") throw new Error(done.error || "Loading failed");
    const outcome = new Map((done.result?.files ?? []).map((f) => [f.id, f]));
    for (const f of state.files.values()) {
      const r = outcome.get(f.id);
      Object.assign(f, r?.ok ? { status: "ready", asset_count: r.asset_count, error: null } : { status: "error", error: r?.error || f.error || "Failed to load" });
    }
    const ready = [...state.files.values()].filter((f) => f.status === "ready");
    const size = ready.reduce((n, f) => n + (outcome.get(f.id)?.pack_size ?? 0), 0);
    updateProgress(0, size, "Downloading previews...", "0%");
    const unpacked = await downloadPacks(
      `${apiBase()}/workspaces/${state.workspace.id}`,
      ready.map((f) => ({ id: f.id, name: f.name, size: outcome.get(f.id)?.pack_size ?? 0 })),
      async (fileId, pack) => {
        await local.putAssets(state.tab, fileId, pack.assets ?? []);
        await local.putItems(state.tab, fileId, "orig", pack.items);
      },
      (received) => updateProgress(received, size, "Downloading previews...", size ? `${Math.floor((received / size) * 100)}%` : undefined),
    );
    // A file whose previews did not arrive is shown as failed; the others open as usual.
    for (const miss of unpacked) {
      const f = state.files.get(miss.id);
      Object.assign(f, { status: "error", error: `Could not download the previews (${miss.error.message})` });
      extraErrors = [...extraErrors, `${f.name}: ${f.error}`];
    }
    if (unpacked.length && unpacked.length === ready.length) throw new Error(`The previews could not be downloaded.\n${extraErrors.join("\n")}`);
    const rec = workspaceRecord();
    await local.saveWorkspaceRecord(rec).catch(() => {});
    state.serverGone = false;
    state.serverReady = new Set(rec.files.filter((f) => f.status === "ready").map((f) => f.id));
    endTask();
    await showWorkspace(rec, { extraErrors });
  } catch (err) {
    endTask(`Failed to open files: ${err.message}`, "error");
    if (!(err instanceof ApiError && err.status === 404)) {
      table.setEmptyMessage(`<div>Failed to open files.</div>`);
      showError("Open Files", err.message);
    }
  } finally {
    state.busy = Math.max(0, state.busy - 1);
    updateActions();
  }
}

// ----- the server's copy ---------------------------------------------------
// The server drops its copy of the files after an hour idle, while the tab
// keeps showing its work. Actions that need the server (Replace, Export, Save,
// copying the full image) send the files again first, into a restored session
// with the same file ids: from this page's memory, from a kept file handle
// (Chromium; after a reload it needs the user's permission), or picked again by
// the user in the Files Needed Again dialog. Replacements are kept in this
// browser and sent along; nothing is reprocessed.

/** File id -> the File opened in this page (gone after a reload). */
const sourceFiles = new Map();
/** What withServer returns when the user cancelled sending the files again. */
const CANCELLED = Symbol("cancelled");

function markServerGone() {
  if (!state.workspace) return;
  state.serverGone = true;
  state.serverReady = new Set();
}

const isExpiredError = (err) => err instanceof ApiError && err.status === 404 && /expired/i.test(err.message);

/**
 * Run a server request that needs `fileIds` on the server. Sends them again
 * when the server's copy is gone (also when the request finds out it is).
 * `what` names the action in the dialog ("the Export"). Returns CANCELLED if the
 * user cancelled.
 */
async function withServer(fileIds, what, fn) {
  if (!(await ensureServer(fileIds, what))) return CANCELLED;
  try {
    return await fn();
  } catch (err) {
    if (!isExpiredError(err)) throw err;
    markServerGone();
    if (!(await ensureServer(fileIds, what))) return CANCELLED;
    return await fn();
  }
}

async function ensureServer(fileIds, what) {
  const ids = [...new Set(fileIds)].filter((id) => state.files.get(id)?.status === "ready");
  if (!state.serverGone && ids.every((id) => state.serverReady.has(id))) return true;
  const missing = ids.filter((id) => state.serverGone || !state.serverReady.has(id));
  const sources = await collectSources(missing, what);
  if (!sources) return false;
  await sendToServer(sources);
  return true;
}

const sameSize = (file, meta) => file && file.size === meta.size;

/** The files to send again: from memory or kept handles, else asked for. */
async function collectSources(ids, what) {
  // Files fetched from the game need nothing from this computer: the server fetches them again.
  ids = ids.filter((id) => !state.files.get(id)?.game);
  const found = new Map();
  const needPermission = new Map();
  for (const id of ids) {
    const meta = state.files.get(id);
    const kept = sourceFiles.get(id);
    if (sameSize(kept, meta)) {
      found.set(id, kept);
      continue;
    }
    const handle = await local.getHandle(state.tab, id).catch(() => null);
    const file = handle ? await fileFromHandle(handle) : null;
    if (file === "prompt") needPermission.set(id, handle);
    else if (sameSize(file, meta)) found.set(id, file);
  }
  if (found.size === ids.length) return found;
  return filesNeededDialog(ids, found, needPermission, what);
}

/** Upload files (and their kept replacements) into the server's session, restoring it first if gone. */
async function sendToServer(sources) {
  uploadInProgress = true;
  const lost = [];
  try {
    if (state.serverGone) {
      const files = [...state.files.values()]
        .filter((f) => f.status === "ready")
        .map((f) => ({ id: f.id, name: f.name, size: f.size, asset_count: f.asset_count, ...(f.game ? { game: f.game } : {}) }));
      const restore = () => api.post(`${apiBase()}/workspaces/restore`, { name: state.workspace.name, files, replace: state.workspace.id });
      let created;
      try {
        created = await restore();
      } catch (err) {
        if (err.status !== 428 || !guestMode()) throw err;
        state.guest.verified = false;
        if (!(await ensureGuestVerified())) throw new Error("Verification was cancelled");
        created = await restore();
      }
      state.workspace = { ...state.workspace, id: created.workspace.id };
      state.partSize = created.part_size;
      state.serverGone = false;
      state.serverReady = new Set();
      await saveRecord();
      // Files fetched from the game: the server fetches them again, nothing is sent from here.
      if (created.job) await refetchGameFiles(created.job, lost);
    }
    const list = [...sources].filter(([id]) => !state.serverReady.has(id));
    const total = list.reduce((n, [, f]) => n + f.size, 0) || 1;
    const failed = await uploadFiles(
      `${apiBase()}/workspaces/${state.workspace.id}`,
      list.map(([id, file]) => ({ id, file })),
      state.partSize,
      (sent) => updateProgress(sent, total, `Sending ${list.length} file(s) again`, `${Math.floor((sent / total) * 100)}%`),
    );
    // The server marks what arrived once, not per file (see settleUploads in the Worker): the edits below need it.
    if (failed.length < list.length) await api.post(`${apiBase()}/workspaces/${state.workspace.id}/uploads/finish`, {});
    const missed = new Map(failed.map((f) => [f.id, f.error]));
    for (const [id, file] of list) {
      if (missed.has(id)) {
        // The file changed or went away since it was opened: ask for it next time.
        if (!(missed.get(id) instanceof ApiError)) sourceFiles.delete(id);
        continue;
      }
      sourceFiles.set(id, file);
      await sendEdits(id, lost);
      state.serverReady.add(id);
    }
    if (failed.length) throw failed[0].error;
    if (lost.length) await dropEdits(lost);
  } finally {
    uploadInProgress = false;
  }
}

/** Put a file's kept replacements back into the restored session; the ones not kept go into `lost`. */
async function sendEdits(id, lost) {
  for (const edit of [...state.edits.values()].filter((e) => e.file_id === id)) {
    const kept = await local.getEditFile(state.tab, id, edit.path_id).catch(() => null);
    if (!kept?.blob) {
      lost.push(edit);
      continue;
    }
    const headers = { "X-File-Name": encodeURIComponent(kept.name || edit.source_name || "replacement"), "X-Created-At": String(edit.created_at), "Content-Type": "application/octet-stream" };
    if (kept.options) headers["X-Texture-Options"] = encodeURIComponent(JSON.stringify(kept.options));
    await xhrUpload("PUT", `${apiBase()}/workspaces/${state.workspace.id}/files/${id}/assets/${edit.path_id}/edit`, kept.blob, { headers });
  }
}

/** Wait for the job that fetches a restored session's game files again, then put their replacements back. */
async function refetchGameFiles(job, lost) {
  const done = await pollJob(job, (j) => updateProgress(j.progress, j.total, j.message && j.message !== "Done" ? j.message : "Fetching the files from the game again", j.total ? `${Math.floor((j.progress / j.total) * 100)}%` : undefined));
  if (done.status === "error") throw new Error(done.error || "Fetching the files from the game failed");
  const info = await api.get(`${apiBase()}/workspaces/${state.workspace.id}`);
  const bad = info.files.filter((f) => state.files.get(f.id)?.game && f.status === "error");
  if (bad.length) throw new Error(`The game no longer has these files as they were opened:\n${bad.map((f) => `${f.name}: ${f.error}`).join("\n")}`);
  for (const file of state.files.values()) {
    if (!file.game || file.status !== "ready") continue;
    await sendEdits(file.id, lost);
    state.serverReady.add(file.id);
  }
}

/** Replacements that could not be sent again (made before they were kept): undo them here. */
async function dropEdits(edits) {
  const names = [];
  for (const edit of edits) {
    const key = `${edit.file_id}:${edit.path_id}`;
    state.edits.delete(key);
    local.deleteItem(state.tab, edit.file_id, edit.path_id, String(edit.created_at)).catch(() => {});
    const asset = state.assets.find((a) => a.key === key);
    if (asset) {
      asset.changed = false;
      names.push(asset.name || key);
    }
  }
  await saveRecord();
  table.refresh();
  showWarning("Replacements Lost", `${names.length} replacement(s) could not be sent to the server again. Replace them again if you still need them.`, names.join("\n"));
}

/**
 * Ask for the files the server needs again. Resolves with id -> File once all
 * are there (it continues by itself), or null (cancelled, or Start Over).
 */
function filesNeededDialog(ids, found, needPermission, what) {
  const dlg = $("#refetchDialog");
  const chosen = new Map(found);
  const problems = new Map(); // id -> text
  let asking = needPermission.size > 0;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      dlg.removeEventListener("hidden.bs.modal", onHidden);
      closeModal(dlg);
      resolve(value);
    };
    const onHidden = () => finish(null);
    const render = () => {
      $("#refetchText").textContent = asking
        ? `The server's copy of these files was removed after an hour without use. Allow ABVME to read them again from your computer to continue ${what}.`
        : `The server's copy of these files was removed after an hour without use. Select them again to continue ${what}. Your previews and replacements are still here.`;
      $("#refetchNote").textContent = asking
        ? "Your browser will ask once for access to the files you opened before."
        : ignored.length
          ? `Not one of these files: ${ignored.join(", ")}`
          : "Files are matched by name and size. Only the files needed now are listed.";
      $("#refetchAllowBtn").classList.toggle("d-none", !asking);
      const select = $("#refetchSelectBtn");
      select.textContent = asking ? "Select Files Instead..." : "Select Files...";
      select.classList.toggle("btn-primary-abv", !asking);
      $("#refetchList").innerHTML = ids
        .map((id) => {
          const f = state.files.get(id);
          const [text, cls] = chosen.has(id) ? ["✓ Ready", "ok"] : problems.has(id) ? [problems.get(id), "bad"] : asking && needPermission.has(id) ? ["Needs permission", ""] : ["Not selected", ""];
          return `<div class="item"><span class="name" title="${escapeHtml(f.name)}">${escapeHtml(f.name)}</span><span class="meta">${formatByteSize(f.size)}</span><span class="st ${cls}">${escapeHtml(text)}</span></div>`;
        })
        .join("");
      if (ids.every((id) => chosen.has(id))) finish(chosen);
    };
    let ignored = [];
    const take = (files) => {
      ignored = [];
      for (const file of files) {
        const id = ids.find((i) => !chosen.has(i) && state.files.get(i).name === file.name);
        if (!id) {
          if (!ids.some((i) => chosen.get(i)?.name === file.name)) ignored.push(file.name);
          continue;
        }
        if (file.size !== state.files.get(id).size) {
          problems.set(id, `Different file (${formatByteSize(file.size)})`);
          continue;
        }
        problems.delete(id);
        chosen.set(id, file);
        sourceFiles.set(id, file);
        if (file.handle) local.putHandle(state.tab, id, file.handle).catch(() => {});
      }
      render();
    };
    $("#refetchAllowBtn").onclick = async () => {
      for (const [id, handle] of needPermission) {
        if (chosen.has(id)) continue;
        const file = await fileFromHandle(handle, { request: true });
        if (file && file !== "prompt" && sameSize(file, state.files.get(id))) chosen.set(id, file);
      }
      asking = false; // whatever is left has to be picked
      render();
    };
    $("#refetchSelectBtn").onclick = async () => {
      const files = await pickFiles({ multiple: true });
      if (files === null) {
        const input = $("#refetchInput");
        input.onchange = () => {
          const picked = Array.from(input.files || []);
          input.value = "";
          take(picked);
        };
        input.click();
      } else if (files.length) take(files);
    };
    $("#refetchStartOverBtn").onclick = async () => {
      if (state.edits.size && !(await askYesNo("Start Over", "Close this workspace and open files again? The replacements you made will be lost."))) return;
      finish(null);
      await closeWorkspace();
      onOpenFilesClicked();
    };
    dlg.addEventListener("hidden.bs.modal", onHidden);
    render();
    if (!settled) openModal(dlg);
  });
}

/** Shown while a job waits for the processing service to start (it sleeps when unused). */
const COLD_START_MESSAGE = "Starting the processing server… (can take up to a minute after a quiet period)";
const COLD_START_AFTER_MS = 3000;

async function pollJob(job, onUpdate) {
  let current = job;
  let delay = 400;
  const started = Date.now();
  while (current.status === "queued" || current.status === "running") {
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(1500, delay + 200);
    current = (await api.get(`${apiBase()}/jobs/${current.id}`)).job;
    // Still queued after a few seconds: the service is starting. Say so instead
    // of "Queued 0/0", with a moving bar; the job's own progress shows once it runs.
    if (current.status === "queued" && Date.now() - started > COLD_START_AFTER_MS) updateProgress(0, 0, COLD_START_MESSAGE);
    else onUpdate?.(current);
  }
  return current;
}

/** Show a workspace kept in this tab. */
async function showWorkspace(rec, { autoFit = true, quiet = false, extraErrors = [] } = {}) {
  state.workspace = rec.ws;
  state.files = new Map(rec.files.map((f) => [f.id, f]));
  state.edits = new Map(rec.edits.map((e) => [`${e.file_id}:${e.path_id}`, e]));
  state.savedEdits = new Set(rec.saved ?? []);
  state.itemCache.clear();
  $("#workspaceName").textContent = rec.ws.name;
  $("#workspaceName").title = `${rec.ws.name} — ${rec.files.length} file(s)`;
  document.title = `${rec.ws.name} - ABVME`;
  const ready = rec.files.filter((f) => f.status === "ready");
  const assets = [];
  for (const file of ready) {
    const rows = (await local.getAssets(state.tab, file.id)) ?? [];
    for (const r of rows) {
      const key = `${file.id}:${r.path_id}`;
      // After a replace the Size column shows the asset's new size (kept with the edit).
      const edit = state.edits.get(key);
      assets.push({ ...r, key, file_id: file.id, source: file.name, changed: Boolean(edit), size: edit?.asset_size ?? r.size, orig_size: r.size });
    }
  }
  state.assets = assets;
  applyAssetFilter(false, autoFit);
  showPlaceholder("Select an asset from the list to view its preview.");
  const errors = rec.files.filter((f) => f.status === "error").map((f) => `${f.name}: ${f.error || "failed to load"}`);
  errors.push(...extraErrors);
  setStatus(`Loaded ${visibleAssets().length.toLocaleString()} asset(s) from ${ready.length} file(s)`);
  if (errors.length && !quiet) showWarning("Open Files", `${errors.length} file(s) could not be loaded.`, errors.join("\n"));
  lastActivity = lastKeepAlive = Date.now();
  updateActions();
}

function visibleAssets() {
  if (state.settings.show_all) return state.assets;
  return state.assets.filter((a) => AVAILABLE_TYPES.includes(a.type));
}

function applyAssetFilter(keepSelection, autoFit = false) {
  table.setEmptyMessage(null);
  table.setAssets(visibleAssets(), { autoFit, keepSelection });
  if (!state.assets.length && state.workspace) table.setEmptyMessage("<div>No assets were found in the loaded files.</div>");
  updateActions();
}

// ======================================================================
// Selection + preview
// ======================================================================

function onSelectionChanged(selected) {
  state.selected = selected;
  updateActions();
  if (!selected.length) showPlaceholder("Select an asset from the list to view its preview.");
  else if (selected.length === 1) showAssetPreview(selected[0]);
}

function setView(which) {
  const views = { image: $("#photoViewer"), text: $("#textView").parentElement, audio: $("#audioView"), mesh: $("#meshView"), placeholder: $("#previewPlaceholder") };
  for (const [name, el] of Object.entries(views)) el.classList.toggle("active", name === which);
  // Leaving the audio view stops the clip; leaving the mesh view frees its buffers.
  if (which !== "audio") stopAudio();
  if (which !== "mesh") meshViewer?.clear();
}

/** The 3D viewer of the Mesh preview, made the first time a mesh is shown. */
let meshViewer = null;
function getMeshViewer() {
  if (meshViewer) return meshViewer;
  meshViewer = new MeshViewer($("#meshView canvas"));
  const wire = $("#meshWire");
  wire.addEventListener("click", () => {
    const on = wire.getAttribute("aria-pressed") !== "true";
    wire.setAttribute("aria-pressed", String(on));
    meshViewer.setWireframe(on);
  });
  $("#meshReset").addEventListener("click", () => meshViewer.resetView());
  return meshViewer;
}

/** Large meshes are not in the pack: their OBJ export is fetched when shown, kept for this tab. */
const meshCache = new Map(); // `${asset.key}:${sig}` -> geometry
const MESH_CACHE_MAX = 4;

async function longMesh(asset) {
  const key = `${asset.key}:${assetSig(asset)}`;
  if (meshCache.has(key)) return meshCache.get(key);
  const file = await exportFile(asset, "the mesh preview");
  if (!file) return null;
  const geometry = geometryFromObj(await file.text());
  meshCache.set(key, geometry);
  while (meshCache.size > MESH_CACHE_MAX) meshCache.delete(meshCache.keys().next().value);
  return geometry;
}

function stopAudio() {
  const player = $("#audioView audio");
  if (!player.getAttribute("src")) return;
  player.pause();
  player.removeAttribute("src");
  player.load();
}

/** Long clips are not in the pack: fetched (as an export) when shown, kept for this tab. */
const audioCache = new Map(); // `${asset.key}:${sig}` -> Blob
const AUDIO_CACHE_MAX = 8;

async function longAudio(asset) {
  const key = `${asset.key}:${assetSig(asset)}`;
  if (audioCache.has(key)) return audioCache.get(key);
  const file = await exportFile(asset, "the audio preview");
  if (!file) return null;
  const blob = new Blob([file], { type: "audio/wav" });
  audioCache.set(key, blob);
  while (audioCache.size > AUDIO_CACHE_MAX) audioCache.delete(audioCache.keys().next().value);
  return blob;
}

function fmtDuration(seconds) {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function showPlaceholder(message) {
  state.previewToken++;
  state.previewAsset = null;
  state.previewItem = null;
  $("#previewPlaceholder").textContent = message;
  setView("placeholder");
  $("#previewBanner").classList.add("d-none");
  $("#previewNote").classList.add("d-none");
  $("#previewInfo").textContent = "";
  $("#dumpView").textContent = "";
  setLoading(false);
}

function setLoading(on, text = "Loading preview…") {
  const stack = $("#previewStack");
  let el = stack.querySelector(".loading-spinner");
  if (on && !el) {
    el = document.createElement("div");
    el.className = "loading-spinner";
    el.innerHTML = `<div class="spin"></div><span></span>`;
    stack.appendChild(el);
  }
  if (el) {
    if (on) el.querySelector("span").textContent = text;
    else el.remove();
  }
}

// Previews and dumps come from this tab's storage (sent by the server when the
// files were opened, or after a replacement). A small LRU keeps recent ones in memory.
const ITEM_CACHE_SIZE = 300;

/** Which version of an asset is current: "orig" or the edit's timestamp. */
const assetSig = (asset) => String(state.edits.get(asset.key)?.created_at ?? "orig");

async function assetItem(asset) {
  const key = `${asset.key}:${assetSig(asset)}`;
  const cache = state.itemCache;
  if (cache.has(key)) {
    const value = cache.get(key);
    cache.delete(key); // most recently used goes last
    cache.set(key, value);
    return value;
  }
  const item = await local.getItem(state.tab, asset.file_id, asset.path_id, assetSig(asset));
  if (!item) return null;
  cache.set(key, item);
  while (cache.size > ITEM_CACHE_SIZE) cache.delete(cache.keys().next().value);
  return item;
}

function assetUrl(asset, tail) {
  return `${apiBase()}/workspaces/${state.workspace.id}/files/${asset.file_id}/assets/${asset.path_id}/${tail}`;
}

/** The shown image is a reduced preview (not the texture's real size). */
function previewScaled() {
  const p = state.previewItem?.preview;
  return Boolean(p && p.kind === "image" && (p.scaled ?? p.mime !== "image/png"));
}

/** The full texture as PNG, exported by the server (uses processing quota). */
async function fullImageBlob(asset) {
  const resp = await withServer([asset.file_id], "copying the full image", () =>
    request("POST", `${apiBase()}/workspaces/${state.workspace.id}/export`, {
      json: { items: [{ file_id: asset.file_id, path_ids: [asset.path_id] }] },
      raw: true,
    }),
  );
  if (resp === CANCELLED) throw new Error("Cancelled");
  refreshBackendStatus();
  return resp.blob();
}

async function copyPreviewImage() {
  const asset = state.previewAsset;
  let source = state.previewItem?.image;
  if (!asset || !source) return;
  if (previewScaled()) {
    const choice = await messageBox({
      title: "Copy Image",
      text: "This preview is reduced. Copy the image as shown, or the full image? (The full image is exported by the server and uses processing quota.)",
      icon: "question",
      buttons: [
        { id: "full", label: "Full image", primary: true },
        { id: "shown", label: "As shown" },
        { id: "cancel", label: "Cancel" },
      ],
    });
    if (choice === "cancel" || choice === "close") return;
    if (choice === "full") {
      beginTask(`Copying the full image of ${asset.name}...`);
      source = fullImageBlob(asset);
    }
  }
  const full = source instanceof Promise;
  try {
    await viewer.copyImage(source);
    if (full) endTask("Full image copied to clipboard");
    else setStatus("Image copied to clipboard");
  } catch (err) {
    if (full) endTask();
    setStatus(`Copy failed: ${err.message}`, "warning");
  }
}

/** The file name Export gives the shown asset (from its container path). */
const exportName = (asset) => asset?.export_name || `${asset?.name || "image"}.png`;

/**
 * Download image is the same file as Export. When Export would give a PNG and the
 * preview is the full-size PNG, the bytes are already here: no server, no quota.
 */
function localDownload() {
  const item = state.previewItem;
  return Boolean(item?.image && item.image.type === "image/png" && !previewScaled() && /\.png$/i.test(exportName(state.previewAsset)));
}

async function downloadPreviewPng() {
  const image = state.previewItem.image;
  const name = exportName(state.previewAsset);
  const target = await pickSaveTarget(name);
  if (target === null) return;
  if (target === undefined) {
    downloadBlob(image, name);
    return;
  }
  try {
    await writeFile(target, image);
    setStatus(`Saved ${target.name}`);
  } catch (err) {
    await discardTarget(target);
    setStatus(`Download failed: ${err.message}`, "error");
    showError("Download Failed", err.message);
  }
}

function setPreviewUrl(url) {
  if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
  state.previewUrl = url;
}

async function showAssetPreview(asset, { force = false } = {}) {
  if (!force && state.previewAsset?.key === asset.key && state.previewItem) return;
  const token = ++state.previewToken;
  state.previewAsset = asset;
  state.previewItem = null;
  $("#previewBanner").classList.add("d-none");
  $("#previewNote").classList.add("d-none");
  $("#previewInfo").textContent = `${asset.type} · ${asset.name || "(none)"}`;
  $("#dumpView").textContent = "";
  try {
    const item = await assetItem(asset);
    if (token !== state.previewToken) return;
    state.previewItem = item;
    if ($('#previewTabs .tab[data-tab="dump"]').classList.contains("active")) fillDump();
    const meta = item?.preview;
    if (!meta) {
      $("#previewPlaceholder").textContent = "Preview is unavailable.";
      setView("placeholder");
      return;
    }
    if (meta.kind === "image" && item.image) {
      const url = URL.createObjectURL(item.image);
      await viewer.setPhoto(url);
      if (token !== state.previewToken) {
        URL.revokeObjectURL(url);
        return;
      }
      setPreviewUrl(url);
      setView("image");
      $("#previewInfo").textContent = `${asset.type} · ${meta.width}×${meta.height}`;
      if (previewScaled()) {
        const note = $("#previewNote");
        const shown = viewer.img.naturalWidth;
        note.textContent =
          shown && shown < meta.width
            ? `Reduced preview (${shown}×${viewer.img.naturalHeight}) — not actual size (${meta.width}×${meta.height}). Export for the full image.`
            : "Compressed preview — not the original image. Export for the full image.";
        note.classList.remove("d-none");
      }
      if (meta.note) {
        const banner = $("#previewBanner");
        banner.textContent = meta.note;
        banner.classList.remove("d-none");
      }
      setStatus(`Showing ${asset.type} preview: ${asset.name}`);
    } else if (meta.kind === "mesh") {
      const viewer = getMeshViewer();
      const counts = `${Number(meta.vertices).toLocaleString()} vertices · ${Number(meta.triangles).toLocaleString()} triangles${meta.submeshes?.length > 1 ? ` · ${meta.submeshes.length} submeshes` : ""}`;
      $("#previewInfo").textContent = `${asset.type} · ${counts}`;
      if (!viewer.supported) {
        $("#previewPlaceholder").textContent = `This browser cannot show 3D (WebGL).\n${counts}`;
        setView("placeholder");
        return;
      }
      setView("mesh");
      let geometry;
      if (item.mesh) geometry = geometryFromPack(await item.mesh.arrayBuffer(), meta);
      else {
        setStatus(`Loading ${asset.name}...`);
        geometry = await longMesh(asset);
        if (!geometry) {
          if (token === state.previewToken) setStatus("Loading the mesh was cancelled", "warning");
          return;
        }
      }
      if (token !== state.previewToken) return;
      viewer.load(geometry);
      setStatus(`Showing Mesh preview: ${asset.name}`);
    } else if (meta.kind === "audio") {
      const view = $("#audioView");
      const player = view.querySelector("audio");
      view.querySelector(".audio-title").textContent = asset.name || "(none)";
      const facts = [fmtDuration(meta.length), meta.frequency ? `${(meta.frequency / 1000).toLocaleString()} kHz` : "", meta.channels === 1 ? "mono" : meta.channels === 2 ? "stereo" : meta.channels ? `${meta.channels} channels` : "", meta.size ? formatByteSize(meta.size) : ""];
      if (meta.subsounds > 1) facts.push(`first of ${meta.subsounds} sounds`);
      view.querySelector(".audio-meta").textContent = facts.filter(Boolean).join(" · ");
      $("#previewInfo").textContent = `${asset.type} · ${fmtDuration(meta.length)}`;
      stopAudio();
      setView("audio");
      let blob = item.audio;
      if (!blob) {
        setStatus(`Loading ${asset.name}...`);
        blob = await longAudio(asset);
        if (token !== state.previewToken) return;
        if (!blob) {
          setStatus("Loading the audio was cancelled", "warning");
          return;
        }
        // A long clip is not decoded when the file is opened, so its size is known only now.
        if (!meta.size) view.querySelector(".audio-meta").textContent += ` · ${formatByteSize(blob.size)}`;
      }
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      player.src = url;
      setStatus(`Showing AudioClip preview: ${asset.name}`);
    } else if (meta.kind === "text") {
      const view = $("#textView");
      const text = previewText(item);
      view.value = text;
      view.scrollTop = 0;
      setView("text");
      $("#previewInfo").textContent = `${asset.type} · ${Number(meta.length).toLocaleString()} chars`;
      if (meta.truncated) {
        const banner = $("#previewBanner");
        banner.textContent = `Showing the first ${text.length.toLocaleString()} of ${Number(meta.length).toLocaleString()} characters. Export the asset to get the full text.`;
        banner.classList.remove("d-none");
      }
      setStatus(`Showing TextAsset preview: ${asset.name}`);
    } else {
      $("#previewPlaceholder").textContent = meta.message && meta.message !== "Preview is unavailable." ? meta.message : "Preview is unavailable.";
      setView("placeholder");
      setStatus(`Preview unavailable for ${asset.type}`);
    }
  } catch (err) {
    if (token !== state.previewToken) return;
    $("#previewPlaceholder").textContent = `An unexpected error occurred during preview:\n${err.message}`;
    setView("placeholder");
    setStatus(`Error showing preview: ${err.message}`, "error");
  }
}

async function fillDump() {
  const asset = state.previewAsset || singleSelected();
  const view = $("#dumpView");
  if (!asset || !state.workspace) {
    view.textContent = "";
    return;
  }
  const item = state.previewAsset?.key === asset.key && state.previewItem ? state.previewItem : await assetItem(asset).catch(() => null);
  if (state.previewAsset?.key === asset.key || singleSelected()?.key === asset.key) view.textContent = item?.dump ?? "Dump is unavailable.";
}

// ======================================================================
// Replace
// ======================================================================

function onReplaceClicked() {
  const asset = singleSelected();
  if (!asset || state.busy) return;
  if (!asset.editable) {
    setStatus(`Replace is not supported for ${asset.type}.`, "warning");
    return;
  }
  pickReplacement(asset);
}

async function pickReplacement(asset) {
  const texture = asset.type === "Texture2D";
  // A texture takes an image of a supported format; a TextAsset takes any file (its bytes become the text).
  const files = await pickFiles({ accept: texture ? { description: "Images", mime: "image/*", extensions: TEXTURE_REPLACE_SUFFIXES, only: true } : null });
  if (files === null) {
    const input = $("#replaceInput");
    input.accept = texture ? ".png,.jpg,.jpeg,.bmp,.tga,.dds,image/*" : "";
    input.click();
  } else if (files.length && singleSelected()?.key === asset.key && !state.busy) {
    startReplace(asset, files[0]);
  }
}

async function startReplace(asset, file) {
  let options = null;
  if (asset.type === "Texture2D" && !TEXTURE_REPLACE_SUFFIXES.includes(suffix(file.name))) {
    showWarning("Replace", `A texture can be replaced with an image: ${TEXTURE_REPLACE_SUFFIXES.join(", ")}.\n'${file.name}' is not one of these.`);
    return false;
  }
  if (asset.type === "Texture2D") {
    if (state.settings.always_show_texture_options) {
      options = await showTextureDialog(asset, file);
      if (!options) return false;
    } else {
      const choice = await messageBox({
        title: "Confirm Replace",
        text: `Replace '${asset.name}' with '${file.name}'?`,
        icon: "question",
        buttons: [
          { id: "options", label: "Options...", flat: true, left: true },
          { id: "yes", label: "Yes", primary: true },
          { id: "no", label: "No" },
        ],
      });
      if (choice === "options") {
        options = await showTextureDialog(asset, file);
        if (!options) return false;
      } else if (choice !== "yes") return false;
    }
  } else if (asset.type === "TextAsset" && !suffixInContainer(file.name, asset.container)) {
    const ok = await askYesNo("Confirm Replace", `The suffix of '${file.name}' is not in the container of '${asset.name}'. Replace anyway?`);
    if (!ok) return false;
  } else if (!(await askYesNo("Confirm Replace", `Replace '${asset.name}' with '${file.name}'?`))) {
    return false;
  }
  return doReplace(asset, file, options);
}

const REPLACE_MAX_BYTES = 90 * 1024 * 1024;

async function doReplace(asset, file, options) {
  if (file.size > REPLACE_MAX_BYTES) {
    showWarning("Replace", `Replacement files can be at most ${REPLACE_MAX_BYTES / 1048576} MB.\n'${file.name}' is ${formatByteSize(file.size)}.`);
    return false;
  }
  state.busy++;
  updateActions();
  beginTask(`Replacing ${asset.name}...`, { determinate: true });
  try {
    const headers = { "X-File-Name": encodeURIComponent(file.name), "Content-Type": "application/octet-stream" };
    if (options) headers["X-Texture-Options"] = encodeURIComponent(JSON.stringify(options));
    // The server checks the replacement and sends back the asset's new preview and dump.
    const resp = await withServer([asset.file_id], "the replacement", () =>
      xhrUpload("POST", assetUrl(asset, "replace"), file, {
        headers,
        binary: true,
        onProgress: (n, total) => updateProgress(n, total, n < total ? `Uploading: ${file.name}` : `Replacing ${asset.name}...`, `${Math.floor((n / total) * 100)}%`),
      }),
    );
    if (resp === CANCELLED) {
      endTask("Replace cancelled");
      return false;
    }
    const edit = JSON.parse(decodeURIComponent(resp.header("X-ABVME-Edit") || "%7B%7D"));
    const { message, ...row } = edit;
    const pack = await readPack(resp.data);
    const newSize = pack.items.find(([pid]) => pid === asset.path_id)?.[1].size;
    if (typeof newSize === "number") row.asset_size = newSize;
    const previous = state.edits.get(asset.key);
    await local.putItems(state.tab, asset.file_id, String(row.created_at), pack.items);
    if (previous) local.deleteItem(state.tab, asset.file_id, asset.path_id, String(previous.created_at)).catch(() => {});
    state.edits.set(asset.key, row);
    asset.changed = true;
    if (asset.orig_size === undefined) asset.orig_size = asset.size;
    if (row.asset_size !== undefined) asset.size = row.asset_size;
    // Kept to send again if the server's copy expires.
    local.putEditFile(state.tab, asset.file_id, asset.path_id, { blob: file, name: file.name, options, created_at: row.created_at }).catch(() => {});
    await saveRecord();
    table.refresh();
    endTask(message || "Replaced");
    await showAssetPreview(asset, { force: true });
    return true;
  } catch (err) {
    endTask(err.message, "error");
    showError("Replace Failed", err.message);
    return false;
  } finally {
    refreshBackendStatus();
    state.busy = Math.max(0, state.busy - 1);
    updateActions();
  }
}

async function revertSelected() {
  const targets = state.selected.filter((a) => a.changed);
  if (!targets.length) return;
  const label = targets.length === 1 ? `'${targets[0].name}'` : `${targets.length} assets`;
  if (!(await askYesNo("Revert Changes", `Discard the replacement for ${label}?`))) return;
  beginTask("Reverting...");
  let failed = 0;
  for (const asset of targets) {
    try {
      // Only the server's session holds the replacement; a gone one needs no undo there.
      if (!state.serverGone && state.serverReady.has(asset.file_id)) {
        try {
          await api.del(assetUrl(asset, "edit"));
        } catch (err) {
          if (!isExpiredError(err)) throw err;
        }
      }
      local.deleteEditFile(state.tab, asset.file_id, asset.path_id).catch(() => {});
      const edit = state.edits.get(asset.key);
      state.edits.delete(asset.key);
      asset.changed = false;
      if (asset.orig_size !== undefined) asset.size = asset.orig_size;
      if (edit) local.deleteItem(state.tab, asset.file_id, asset.path_id, String(edit.created_at)).catch(() => {});
    } catch {
      failed++;
    }
  }
  await saveRecord();
  table.refresh();
  endTask(failed ? `Failed to revert ${failed} asset(s)` : `Reverted ${targets.length} asset(s)`, failed ? "error" : "info");
  const one = singleSelected();
  if (one) showAssetPreview(one, { force: true });
  updateActions();
}

// ----- Texture Replace Options dialog ------------------------------------

let textureResolve = null;

function bindTextureDialog() {
  const fill = (sel, labels) => ($(sel).innerHTML = labels.map((l, i) => `<option value="${i}">${l}</option>`).join(""));
  $("#texFormat").innerHTML = TEXTURE_FORMAT_CHOICES.map(([l, v]) => `<option value="${v ?? ""}">${l}</option>`).join("");
  fill("#texFilter", FILTER_MODE_LABELS);
  fill("#texWrapU", WRAP_MODE_LABELS);
  fill("#texWrapV", WRAP_MODE_LABELS);
  fill("#texColor", COLOR_SPACE_LABELS);
  const dlg = $("#textureDialog");
  dlg.addEventListener("click", (e) => {
    const r = e.target.closest("[data-result]");
    if (!r) return;
    if (r.dataset.result === "replace") {
      const lightmap = Number.parseInt($("#texLightmap").value.replace(/^0x/i, ""), 16);
      if (Number.isNaN(lightmap) || lightmap < 0) {
        showWarning("Texture Replace Options", "Lightmap format must be a hexadecimal number (e.g. 0x6).");
        return;
      }
      const base = dlg._options;
      const fmt = $("#texFormat").value;
      textureResolve?.({
        target_format: fmt === "" ? null : Number(fmt),
        has_mip_maps: $("#texMips").checked,
        mipmap_count: base.mipmap_count,
        filter_mode: Number($("#texFilter").value),
        aniso: Math.max(0, Math.min(16, Number.parseInt($("#texAniso").value, 10) || 0)),
        mip_bias: Math.max(-16, Math.min(16, Number($("#texBias").value) || 0)),
        wrap_u: Number($("#texWrapU").value),
        wrap_v: Number($("#texWrapV").value),
        lightmap_format: lightmap,
        color_space: $("#texColor").disabled ? base.color_space : Number($("#texColor").value),
      });
      if (state.settings.always_show_texture_options !== $("#texAlways").checked) setSetting("always_show_texture_options", $("#texAlways").checked);
    } else textureResolve?.(null);
    textureResolve = null;
    closeModal(dlg);
  });
  dlg.addEventListener("hidden.bs.modal", () => {
    textureResolve?.(null);
    textureResolve = null;
    for (const url of [dlg._newUrl, dlg._oldUrl]) if (url) URL.revokeObjectURL(url);
    dlg._newUrl = dlg._oldUrl = null;
  });
}

async function showTextureDialog(asset, file) {
  // Options and the current preview were sent with the asset (no request needed).
  const item = await assetItem(asset).catch(() => null);
  const options = item?.texture_options;
  if (!options) {
    showError("Texture Replace Options", "Texture options are not available for this asset.");
    return null;
  }
  const oldUrl = item.image ? URL.createObjectURL(item.image) : null;
  const clamp = (v, max) => String(Math.max(0, Math.min(max, Number(v) || 0)));
  const dlg = $("#textureDialog");
  dlg._options = options;
  dlg._oldUrl = oldUrl;
  $("#texName").textContent = asset.name;
  $("#texName").title = asset.name;
  $("#texFormat").value = options.target_format == null ? "" : String(options.target_format);
  $("#texMips").checked = Boolean(options.has_mip_maps);
  $("#texFilter").value = clamp(options.filter_mode, FILTER_MODE_LABELS.length - 1);
  $("#texAniso").value = clamp(options.aniso, 16);
  $("#texBias").value = Number(options.mip_bias || 0).toFixed(2);
  $("#texWrapU").value = clamp(options.wrap_u, WRAP_MODE_LABELS.length - 1);
  $("#texWrapV").value = clamp(options.wrap_v, WRAP_MODE_LABELS.length - 1);
  $("#texLightmap").value = `0x${Number(options.lightmap_format || 0).toString(16)}`;
  $("#texColor").disabled = options.color_space == null;
  if (options.color_space != null) $("#texColor").value = clamp(options.color_space, COLOR_SPACE_LABELS.length - 1);
  $("#texAlways").checked = Boolean(state.settings.always_show_texture_options);
  $("#texNewName").textContent = `[ ${file.name} ]`;
  const setPreview = (el, url) => {
    el.style.backgroundImage = url ? `url("${url}")` : "";
    el.textContent = url ? "" : "(none)";
  };
  setPreview($("#texOld"), oldUrl);
  const newUrl = URL.createObjectURL(file);
  dlg._newUrl = newUrl;
  const probe = new Image();
  probe.onload = () => setPreview($("#texNew"), newUrl);
  probe.onerror = () => setPreview($("#texNew"), null);
  setPreview($("#texNew"), null);
  probe.src = newUrl;
  return new Promise((resolve) => {
    textureResolve = resolve;
    bootstrap.Modal.getOrCreateInstance(dlg).show();
  });
}

// ======================================================================
// Export
// ======================================================================

/** A TextAsset's exact bytes from the pack the tab holds (what Export would write), or null. */
async function localTextFile(asset) {
  if (asset.type !== "TextAsset") return null;
  const item = await assetItem(asset).catch(() => null);
  return item?.raw instanceof Blob ? item.raw : null;
}

async function onExportClicked() {
  if (state.busy) return;
  // Assets of a type that cannot be exported are left out (the button needs at least one that can).
  const selected = state.selected.filter((a) => a.exportable);
  const skipped = state.selected.length - selected.length;
  if (state.selected.length && !selected.length) {
    const types = [...new Set(state.selected.map((a) => a.type))].join(", ");
    setStatus(`Export not supported for ${types}.`, "warning");
    showWarning("Export Not Supported", `Export not supported for ${types} currently.`);
    return;
  }
  if (!selected.length) {
    setStatus("Select assets to export.", "warning");
    showInfo("No Assets Selected", "Select at least one asset to export.");
    return;
  }
  const byFile = new Map();
  for (const a of selected) {
    if (!byFile.has(a.file_id)) byFile.set(a.file_id, []);
    byFile.get(a.file_id).push(a.path_id);
  }
  const zipName = zipFileName("export");
  // Ask where to save first, like the desktop app: a cancel costs no processing time.
  const target = await pickSaveTarget(selected.length === 1 ? exportName(selected[0]) : zipName);
  if (target === null || state.busy) return;
  state.busy++;
  updateActions();
  beginTask(selected.length === 1 ? `Exporting ${selected[0].name}...` : `Exporting ${selected.length} assets...`);
  try {
    let filename;
    let errors = [];
    let blob = selected.length === 1 ? await localTextFile(selected[0]) : null;
    if (blob) {
      filename = exportName(selected[0]);
    } else {
      const resp = await withServer([...byFile.keys()], "the Export", () =>
        request("POST", `${apiBase()}/workspaces/${state.workspace.id}/export`, {
          json: { items: [...byFile].map(([file_id, path_ids]) => ({ file_id, path_ids })) },
          raw: true,
        }),
      );
      if (resp === CANCELLED) {
        if (target) await discardTarget(target);
        endTask("Export cancelled");
        return;
      }
      filename = decodeURIComponent(resp.headers.get("x-abvme-filename") || "export.bin");
      errors = JSON.parse(decodeURIComponent(resp.headers.get("x-abvme-errors") || "%5B%5D"));
      blob = await resp.blob();
    }
    if (target) await writeFile(target, blob);
    else downloadBlob(blob, filename === "export.zip" ? zipName : filename);
    if (selected.length === 1) {
      endTask(`Exported: ${target?.name ?? filename}${skipped ? ` (${skipped} asset(s) of a type that cannot be exported were left out)` : ""}`);
    } else {
      const ok = selected.length - errors.length;
      let message = `Successfully exported ${ok} asset(s).`;
      if (errors.length) message += ` Failed to export ${errors.length} asset(s).`;
      if (skipped) message += ` ${skipped} asset(s) of a type that cannot be exported were left out.`;
      endTask(message, errors.length ? "warning" : "info");
      (errors.length ? showWarning : showInfo)("Export Completed", message, errors.join("\n"));
    }
  } catch (err) {
    if (target) await discardTarget(target);
    endTask(`Export failed: ${err.message}`, "error");
    showError("Export Failed", err.message);
  } finally {
    state.busy = Math.max(0, state.busy - 1);
    updateActions();
    refreshBackendStatus();
  }
}

// ======================================================================
// Save dialog
// ======================================================================

const saveSel = { items: [], selected: new Set(), anchor: -1 };

function bindSaveDialog() {
  const fill = (sel, key) => ($(sel).innerHTML = OPTION_LABELS[key].map(([l, v]) => `<option value="${v}">${l}</option>`).join(""));
  fill("#savePacker", "packer");
  fill("#saveResource", "resource_patch_mode");
  fill("#saveCrc", "crc_mode");
  for (const tip of $$(".help-tip")) {
    tip.title = SAVE_OPTION_HELP[tip.dataset.help];
    tip.setAttribute("tabindex", "0");
    new bootstrap.Tooltip(tip, { customClass: "abv-tooltip", placement: "left" });
  }
  $("#saveOptionsToggle").addEventListener("click", () => {
    const open = !state.settings.save_options_expanded;
    setSetting("save_options_expanded", open);
    syncSaveOptionsToggle();
  });
  $("#saveOnlyChanged").addEventListener("change", (e) => {
    setSetting("show_only_changed_files", e.target.checked);
    renderSaveList();
  });
  const list = $("#saveFileList");
  list.addEventListener("click", (e) => {
    const item = e.target.closest(".item[data-index]");
    if (!item) return;
    const i = Number(item.dataset.index);
    const id = saveSel.items[i].id;
    if (e.shiftKey && saveSel.anchor >= 0) {
      if (!(e.ctrlKey || e.metaKey)) saveSel.selected.clear();
      const [lo, hi] = [Math.min(i, saveSel.anchor), Math.max(i, saveSel.anchor)];
      for (let k = lo; k <= hi; k++) saveSel.selected.add(saveSel.items[k].id);
    } else if (e.ctrlKey || e.metaKey || matchMedia("(pointer: coarse)").matches) {
      saveSel.selected.has(id) ? saveSel.selected.delete(id) : saveSel.selected.add(id);
      saveSel.anchor = i;
    } else {
      saveSel.selected = new Set([id]);
      saveSel.anchor = i;
    }
    renderSaveList(false);
  });
  list.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
      e.preventDefault();
      saveSel.selected = new Set(saveSel.items.map((f) => f.id));
      renderSaveList(false);
    }
  });
  $("#saveSelectedBtn").addEventListener("click", async () => {
    const ids = saveSel.items.filter((f) => saveSel.selected.has(f.id)).map((f) => f.id);
    if (!ids.length) return;
    let outName;
    let dest;
    if (ids.length === 1) {
      const file = state.files.get(ids[0]);
      dest = await pickSaveTarget(file.name);
      if (dest === null) return;
      outName = dest ? dest.name : await promptText("Save As...", "File name", file.name, { okLabel: "Save" });
      if (!outName) return;
    }
    closeModal($("#saveDialog"));
    runSave({ file_ids: ids, out_name: outName }, dest);
  });
  $("#saveAllBtn").addEventListener("click", async () => {
    // One changed file comes back as one bundle: ask where it goes first.
    const changed = [...changedFileIds()];
    let dest;
    if (changed.length === 1) {
      dest = await pickSaveTarget(state.files.get(changed[0])?.name || "bundle");
      if (dest === null) return;
    }
    closeModal($("#saveDialog"));
    runSave({ only_changed: true, out_name: dest?.name }, dest);
  });
}

function syncSaveOptionsToggle() {
  const open = Boolean(state.settings.save_options_expanded);
  $("#saveOptionsToggle").setAttribute("aria-expanded", String(open));
  $("#saveOptionsPanel").classList.toggle("d-none-important", !open);
}

function changedFileIds() {
  return new Set([...state.edits.values()].map((e) => e.file_id));
}

function renderSaveList(rebuild = true) {
  const changed = changedFileIds();
  if (rebuild) {
    const all = [...state.files.values()];
    saveSel.items = state.settings.show_only_changed_files ? all.filter((f) => changed.has(f.id)) : all;
    const ids = new Set(saveSel.items.map((f) => f.id));
    for (const id of [...saveSel.selected]) if (!ids.has(id)) saveSel.selected.delete(id);
  }
  const list = $("#saveFileList");
  if (!state.files.size) list.innerHTML = `<div class="item is-placeholder">No files loaded</div>`;
  else if (!saveSel.items.length) list.innerHTML = `<div class="item is-placeholder">No changed files</div>`;
  else {
    list.innerHTML = saveSel.items
      .map((f, i) => {
        const isChanged = changed.has(f.id);
        const cls = ["item"];
        if (isChanged) cls.push("changed");
        if (saveSel.selected.has(f.id)) cls.push("selected");
        if (f.status !== "ready") cls.push("error");
        const title = `${f.name}${isChanged ? " (modified)" : ""}${f.error ? ` — ${f.error}` : ""}`;
        return `<div class="${cls.join(" ")}" data-index="${i}" title="${escapeHtml(title)}">${isChanged ? "* " : ""}${escapeHtml(f.name)}<span class="meta">${formatByteSize(f.size)}</span></div>`;
      })
      .join("");
  }
  $("#saveSelectedBtn").disabled = !saveSel.selected.size;
  $("#saveAllBtn").disabled = !changed.size;
}

function openSaveDialog() {
  $("#savePacker").value = state.settings.packer;
  $("#saveResource").value = state.settings.resource_patch_mode;
  $("#saveCrc").value = state.settings.crc_mode;
  $("#saveOnlyChanged").checked = Boolean(state.settings.show_only_changed_files);
  syncSaveOptionsToggle();
  saveSel.selected.clear();
  saveSel.anchor = -1;
  renderSaveList();
  openModal($("#saveDialog"), { onShown: () => $("#saveFileList").focus() });
}

/** dest: a picked file for a one-file save (see files.js), written once the bundle is built. */
async function runSave(target, dest) {
  const body = {
    ...target,
    packer: $("#savePacker").value,
    resource_patch_mode: $("#saveResource").value,
    crc_mode: $("#saveCrc").value,
  };
  state.busy++;
  updateActions();
  beginTask("Saving...", { determinate: true });
  try {
    const fileIds = target.file_ids ?? [...changedFileIds()];
    const started = await withServer(fileIds, "the Save", () => api.post(`${apiBase()}/workspaces/${state.workspace.id}/save`, body));
    if (started === CANCELLED) {
      if (dest) await discardTarget(dest);
      endTask("Save cancelled");
      return;
    }
    // reused: nothing changed since the last save and its files are still on
    // the server, which hands that save back instead of building it again.
    const { job, reused } = started;
    const done = await pollJob(job, (j) => updateProgress(j.progress, j.total, j.message && j.message !== "Done" ? j.message : null));
    if (done.status === "error") throw new Error(done.error || "Save failed");
    const files = done.result.files;
    const ok = files.filter((f) => f.ok);
    const failed = files.filter((f) => !f.ok);
    // The replacements of the files written are saved (Close and Open stop asking about them).
    const savedFiles = new Set(ok.map((f) => f.id));
    for (const [key, e] of state.edits) if (savedFiles.has(e.file_id)) state.savedEdits.add(`${key}@${e.created_at}`);
    saveRecord();
    const warnings = done.result.warnings || [];
    let message = ok.length === 1 && files.length === 1 ? `Successfully saved ${ok[0].name}` : `Successfully saved ${ok.length} file(s)`;
    if (reused) message += " (nothing changed since the last save, so its files are used again)";
    if (warnings.length) message += ` (${warnings.join("; ")})`;
    if (failed.length) message += `\nFailed: ${failed.map((f) => `${f.name} — ${f.error}`).join("; ")}`;
    const url = ok.length === 1 && files.length === 1 ? `${apiBase()}/jobs/${done.id}/download?n=${ok[0].index}` : null;
    if (url && dest) {
      try {
        await writeFile(dest, await request("GET", url, { raw: true }));
      } catch (err) {
        // The bundle is built: it can still be downloaded from the result dialog.
        await discardTarget(dest);
        message += `\nCould not write ${dest.name}: ${err.message}`;
        warnings.push("not written");
      }
    } else if (url) downloadUrl(url, ok[0].name);
    else if (dest) await discardTarget(dest);
    endTask(message.split("\n")[0], failed.length ? "error" : warnings.length ? "warning" : "info");
    showSaveResult(done, message, failed.length ? "critical" : warnings.length ? "warning" : "information");
  } catch (err) {
    if (dest) await discardTarget(dest);
    endTask(`Save failed: ${err.message}`, "error");
    showError("Save Failed", err.message);
  } finally {
    state.busy = Math.max(0, state.busy - 1);
    updateActions();
    refreshBackendStatus();
  }
}

/** A zip named after the workspace: "<workspace name>_<kind>.zip". */
function zipFileName(kind) {
  return `${(state.workspace?.name || kind).replace(/[\\/:*?"<>|]/g, "_")}_${kind}.zip`;
}

/** Save one result of a save job: Save As where the browser has it, else a download. */
async function saveJobFile(url, name) {
  const target = await pickSaveTarget(name);
  if (target === null) return;
  if (target === undefined) {
    downloadUrl(url, name);
    return;
  }
  try {
    await writeFile(target, await request("GET", url, { raw: true }));
    setStatus(`Saved ${target.name}`);
  } catch (err) {
    await discardTarget(target);
    setStatus(`Download failed: ${err.message}`, "error");
    showError("Download Failed", err.message);
  }
}

function showSaveResult(job, message, icon) {
  const dlg = $("#saveResultDialog");
  dlg.querySelector(".title-text").textContent = icon === "critical" ? "Save Failed" : "Save Complete";
  const ic = dlg.querySelector(".msgbox-icon");
  ic.className = `msgbox-icon ${icon}`;
  ic.textContent = icon === "critical" ? "×" : icon === "warning" ? "!" : "i";
  dlg.querySelector(".msgbox-text").textContent = message;
  const list = dlg.querySelector(".save-results");
  list.innerHTML = job.result.files
    .map((f) =>
      f.ok
        ? `<div class="item"><span class="name" title="${escapeHtml(f.name)}">${escapeHtml(f.name)}</span><span class="meta">${formatByteSize(f.size)}</span>
             <a class="btn btn-abv icon-btn" href="${apiBase()}/jobs/${job.id}/download?n=${f.index}" download="${escapeHtml(f.name)}" title="Download ${escapeHtml(f.name)}" aria-label="Download ${escapeHtml(f.name)}"><img class="ico" src="/icons/download-solid.svg" alt="" /></a></div>`
        : `<div class="item error"><span class="name">${escapeHtml(f.name)}</span><span class="meta">${escapeHtml(f.error || "failed")}</span></div>`,
    )
    .join("");
  const zip = $("#saveZipBtn");
  zip.classList.toggle("d-none", !job.has_zip);
  const savedZip = zipFileName("saved");
  zip.onclick = () => saveJobFile(`${apiBase()}/jobs/${job.id}/download?zip=1&name=${encodeURIComponent(savedZip)}`, savedZip);
  for (const a of list.querySelectorAll("a[download]")) {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      saveJobFile(a.getAttribute("href"), a.getAttribute("download"));
    });
  }
  const idle = state.status?.limits?.idle_minutes ?? 60;
  $("#saveNote").textContent = `Download your files now: they are deleted from the server after ${idle} minutes without use, and saving again replaces them.`;
  openModal(dlg);
}

// ======================================================================
// Drag & drop
// ======================================================================

function bindDragDrop() {
  let depth = 0;
  const canReplace = () => Boolean(singleSelected()?.editable) && !state.busy;
  const anyModalOpen = () => Boolean($(".modal.show"));
  window.addEventListener("dragenter", (e) => {
    if (!e.dataTransfer?.types?.includes("Files") || $("#appView").classList.contains("d-none") || anyModalOpen()) return;
    e.preventDefault();
    depth++;
    const decision = state.busy ? { action: DropAction.REJECT, title: "Cannot drop", lines: ["Please wait for the current task to finish"] } : previewDecision(e.dataTransfer, singleSelected(), canReplace());
    if (decision) overlay.show(decision);
  });
  window.addEventListener("dragover", (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = $("#appView").classList.contains("d-none") || anyModalOpen() ? "none" : "copy";
  });
  window.addEventListener("dragleave", () => {
    depth = Math.max(0, depth - 1);
    if (!depth) overlay.clear();
  });
  window.addEventListener("drop", async (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    depth = 0;
    overlay.clear();
    if ($("#appView").classList.contains("d-none") || anyModalOpen() || state.busy) return;
    const files = await filesFromDrop(e.dataTransfer);
    const selected = singleSelected();
    const decision = await classifyDrop(files, selected, canReplace());
    if (decision.action === DropAction.REJECT) {
      setStatus(`${decision.title}: ${decision.detail}`, "warning");
      return;
    }
    if (decision.action === DropAction.OPEN) {
      openFiles(files);
      return;
    }
    if (selected) startReplace(selected, files[0]);
  });
}

// ======================================================================
// Keyboard shortcuts
// ======================================================================

function bindShortcuts() {
  document.addEventListener("keydown", (e) => {
    if ($("#appView").classList.contains("d-none")) return;
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const typing = e.target.closest?.("input, textarea, select, [contenteditable]");
    if ($(".modal.show")) return;
    const key = e.key.toLowerCase();
    let action = null;
    if (key === "o" && e.shiftKey) action = "open-folder";
    else if (key === "o") action = "open-files";
    else if (key === "s" && e.shiftKey) action = "save";
    else if (key === "r" && !e.shiftKey) action = "replace";
    else if (key === "e") action = "export";
    else if (key === "a" && !typing && !e.target.closest?.(".dump-view")) action = "select-all";
    if (!action) return;
    if (typing && action !== "open-files" && action !== "open-folder") return;
    e.preventDefault();
    const el = $(`[data-action="${action}"]`);
    if (el && (el.disabled || el.classList.contains("disabled"))) return;
    runAction(action);
  });
}

// ======================================================================
// Backend status, About, Workspaces, Account
// ======================================================================

const STATUS_POLL_MS = 5 * 60_000;
let lastStatusAt = 0;
let statusRequest = 0;

async function refreshBackendStatus() {
  const dot = $("#backendDot");
  lastStatusAt = Date.now();
  const request = ++statusRequest;
  try {
    const status = await api.get(guestMode() ? "/api/guest/status" : "/api/status");
    // An older request answering after a newer one must not put back an older quota.
    if (request !== statusRequest) return state.status;
    state.status = status;
    if (state.guest) state.guest.quota = status.quota;
    updateQuotaBadge(status.quota);
    showNotice(status.notice);
    const b = status.backend;
    dot.className = `backend-dot ${b.ok ? "ok" : "down"}`;
    dot.title = b.ok ? `Service online · UnityPy ${b.unitypy} · ${b.latency_ms} ms` : `Service unavailable: ${b.error || "unknown error"}`;
    dot.setAttribute("aria-label", dot.title);
    return status;
  } catch (err) {
    if (request !== statusRequest) return state.status;
    dot.className = "backend-dot down";
    dot.title = err.message;
    return null;
  }
}

const NOTICE_HIDDEN_KEY = "abvme.noticeHidden";

/**
 * The announcement and maintenance message set on the admin pages. An
 * announcement can be closed; it stays hidden until its text changes. The
 * maintenance message cannot be closed.
 */
function showNotice(notice) {
  const bar = $("#noticeBar");
  const maintenance = notice?.maintenance || "";
  const announcement = notice?.announcement || "";
  const hidden = !maintenance && announcement === storageGet(localStorage, NOTICE_HIDDEN_KEY);
  const text = hidden ? "" : maintenance || announcement;
  bar.querySelector(".text").textContent = text;
  bar.dataset.announcement = maintenance ? "" : announcement;
  bar.classList.toggle("maintenance", !!maintenance);
  bar.classList.toggle("d-none", !text);
}

$("#noticeClose").addEventListener("click", () => {
  const bar = $("#noticeBar");
  if (bar.dataset.announcement) storageSet(localStorage, NOTICE_HIDDEN_KEY, bar.dataset.announcement);
  bar.classList.add("d-none");
});

/** "1.3.0 (09259af)": the deployed version and its commit. */
const fmtVersion = (s) => (s.commit ? `${s.version} (${s.commit})` : s.version);

function openAbout() {
  const s = state.status;
  $("#aboutVersion").textContent = s ? `Web v${fmtVersion(s)}` : "Web";
  $("#aboutCore").textContent = s?.backend?.unitypy ? `UnityPy ${s.backend.unitypy}` : "UnityPy";
  openModal($("#aboutDialog"));
}

async function openStatusDialog() {
  const grid = $("#statusGrid");
  const render = (s) => {
    if (!s) {
      grid.innerHTML = `<span class="k">Status</span><span>Unavailable</span>`;
      return;
    }
    const b = s.backend;
    const q = s.quota;
    grid.innerHTML = `
      <span class="k">Web app</span><span>v${escapeHtml(fmtVersion(s))} · online</span>
      <span class="k">Service</span><span style="color:${b.ok ? "var(--abv-changed)" : "#ff8a80"}">${b.ok ? "Online" : "Offline"}${b.ok ? ` · ${b.latency_ms} ms` : ""}</span>
      ${b.ok ? `<span class="k">UnityPy</span><span>${escapeHtml(b.unitypy)}</span>` : `<span class="k">Error</span><span>${escapeHtml(b.error || "")}</span>`}
      <span class="k">Account</span><span>${guestMode() ? "Guest (not signed in)" : escapeHtml(state.user?.username ?? "")}</span>
      <span class="k">Quota left</span><span>${q.unlimited ? "Unlimited" : `${fmtPercent(q.percent)} today · resets ${escapeHtml(fmtDate(q.resets_at))}`}</span>
      <span class="k">Quota</span><span>Processing time on the server, used when opening files (preparing previews), replacing, exporting and saving. Viewing previews and dumps uses none.</span>
      ${
        guestMode()
          ? `<span class="k">Per Open</span><span>Up to ${formatByteSize(s.limits.open_bytes)} of files, replacements included</span>`
          : `<span class="k">Max file size</span><span>${formatByteSize(s.limits.max_file)}</span>`
      }
      <span class="k">Workspace</span><span>Kept in this browser tab. Files on the server are deleted after ${s.limits.idle_minutes} minutes without use.</span>`;
  };
  render(state.status);
  $("#statusRefresh").onclick = async () => {
    grid.style.opacity = 0.5;
    render(await refreshBackendStatus());
    grid.style.opacity = 1;
  };
  openModal($("#statusDialog"));
  $("#statusRefresh").onclick();
}

// ----- row actions in dialog tables -----------------------------------------
// The action column is three icon buttons wide. A row shows its everyday
// actions (`inline`) as icons; the rest, and any that do not fit, go in a
// "more" menu that takes the last slot.
const ACTION_SLOTS = 3;

function splitActions(actions) {
  let inline = actions.filter((a) => a.inline);
  let menu = actions.filter((a) => !a.inline);
  if (inline.length + (menu.length ? 1 : 0) > ACTION_SLOTS) {
    const keep = ACTION_SLOTS - 1;
    menu = [...inline.slice(keep), ...menu];
    inline = inline.slice(0, keep);
  }
  return { inline, menu };
}

function rowActionsHtml(actions) {
  if (!actions.length) return "";
  const { inline, menu } = splitActions(actions);
  const button = (attrs, label, icon) =>
    `<button type="button" class="btn btn-abv icon-btn" ${attrs} title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}"><img class="ico" src="/icons/${icon}.svg" alt="" /></button>`;
  return `<div class="row-actions">${inline.map((a) => button(`data-op="${a.op}"`, a.label, a.icon)).join("")}${
    menu.length ? button('data-row-menu aria-haspopup="menu" aria-expanded="false"', "More actions", "ellipsis-solid") : ""
  }</div>`;
}

/**
 * Replace a dialog table's rows. Focus on a row button moves to the same
 * button of the re-rendered row (or the dialog), so the keyboard and Escape
 * keep working after an action reloads the table.
 */
function renderRows(body, html) {
  const active = body.contains(document.activeElement) ? document.activeElement : null;
  const rowId = active?.closest("tr")?.dataset.id;
  const key = active?.dataset.op ? `[data-op="${active.dataset.op}"]` : active?.hasAttribute("data-row-menu") ? "[data-row-menu]" : "";
  body.innerHTML = html;
  if (!active) return;
  const again = rowId && key ? body.querySelector(`tr[data-id="${CSS.escape(rowId)}"] ${key}`) : null;
  (again || body.closest(".modal"))?.focus();
}

/** Handle a click on a rowActionsHtml button. Returns true when it was one. */
function onRowAction(e, actionsFor, run) {
  const btn = e.target.closest("button[data-op], button[data-row-menu]");
  const row = btn?.closest("tr");
  if (!row) return false;
  if (btn.dataset.op) run(btn.dataset.op, row);
  else {
    const items = splitActions(actionsFor(row)).menu.map((a) => ({ ...a, action: () => run(a.op, row) }));
    contextMenu(0, 0, items, { anchor: btn });
  }
  return true;
}

const SESSION_ACTIONS = [{ op: "revoke", label: "Sign out this device", icon: "right-from-bracket-solid", inline: true }];

/**
 * A dialog whose body is a form. `submit` runs on Enter / the primary button;
 * when it throws, its message shows in the dialog's error box and the dialog
 * stays open. A function it returns runs after the dialog closes. Returns open(): resets the form and shows the dialog.
 */
function formDialog(el, submit) {
  const form = el.querySelector("form");
  const errorEl = el.querySelector(".form-error");
  const submitBtn = form.querySelector('[type="submit"]');
  const setError = (message) => {
    errorEl.textContent = message || "";
    errorEl.classList.toggle("d-none", !message);
  };
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    setError("");
    submitBtn.disabled = true;
    try {
      const then = await submit();
      closeModal(el);
      then?.();
    } catch (err) {
      setError(err.message);
    } finally {
      submitBtn.disabled = false;
    }
  });
  return () => {
    form.reset();
    setError("");
    return openModal(el, { onShown: () => form.querySelector("input")?.focus() });
  };
}

function checkNewPassword(password, confirm) {
  if (password.length < 8) throw new Error("The password must be at least 8 characters.");
  if (password !== confirm) throw new Error("The passwords do not match.");
}

function bindAccountDialogs() {
  const openPassword = formDialog($("#passwordDialog"), async () => {
    const next = $("#pwNew").value;
    checkNewPassword(next, $("#pwConfirm").value);
    await api.post("/api/account/password", { current: $("#pwCurrent").value, next });
    loadSessions();
    return () => showInfo("Account", "Password changed. Other devices were signed out.");
  });
  $("#pwOpen").addEventListener("click", openPassword);
  $("#sessionsBody").addEventListener("click", (e) => {
    onRowAction(
      e,
      () => SESSION_ACTIONS,
      async (op, row) => {
        await api.del(`/api/account/sessions/${row.dataset.id}`).catch((err) => showError("Sessions", err.message));
        loadSessions();
      },
    );
  });
}

async function loadSessions() {
  const body = $("#sessionsBody");
  try {
    const { sessions } = await api.get("/api/account/sessions");
    renderRows(
      body,
      sessions
      .map(
        (s) => `<tr class="${s.current ? "current-row" : ""}" data-id="${s.id}">
          <td class="cell-title" title="${escapeHtml(s.user_agent || "")}">${escapeHtml(deviceName(s.user_agent || ""))}${s.current ? ' <span class="badge badge-abv">this device</span>' : ""}</td>
          <td data-label="IP">${escapeHtml(s.ip || "")}</td><td data-label="Last seen" title="${escapeHtml(fmtDate(s.last_seen_at))}">${relTime(s.last_seen_at)}</td>
          <td data-label="Expires" title="${escapeHtml(fmtDate(s.expires_at))}">${relTime(s.expires_at)}</td>
          <td class="cell-actions col-actions">${rowActionsHtml(s.current ? [] : SESSION_ACTIONS)}</td></tr>`,
        )
        .join(""),
    );
  } catch (err) {
    body.innerHTML = `<tr><td colspan="5" style="color:#ff8a80">${escapeHtml(err.message)}</td></tr>`;
  }
}

function openAccountDialog() {
  const u = state.user;
  $("#accountInfo").innerHTML = `
    <span class="k">Username</span><span>${escapeHtml(u.username)}</span>
    <span class="k">Role</span><span>${u.role === "admin" ? "Administrator" : "User"}</span>
    <span class="k">Member since</span><span>${escapeHtml(fmtDate(u.created_at))}</span>
    <span class="k">Quota left</span><span>${state.status?.quota ? (state.status.quota.unlimited ? "Unlimited" : `${fmtPercent(state.status.quota.percent)} today`) : "—"}</span>`;
  openModal($("#accountDialog"));
  loadSessions();
}

boot();
